
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Button, InputField, colors, CircularLoader, TextAreaField, IconDragHandle24, AlertBar, IconView16 } from "@dhis2/ui";
import { useDataEngine, useDataQuery } from "@dhis2/app-runtime";
import RatingInput from "../leadership/components/RatingInput";
import CommentInput from "../leadership/components/CommentInput";
import { CONFIG } from '../leadership/api/LeadershipServices';
import ExpandableStaffAssessment from './components/ExpandableStaffAssessment';
import ManagerRatingInput from './components/ManagerRatingInput';
import ManagerCompetencyReview from './components/ManagerCompetencyReview';
import StaffFinalizationView from './components/StaffFinalizationView';
import RatingScaleAdmin from './components/RatingScaleAdmin';
import RichTextEditor from './components/RichTextEditor';

const richTextStyles = `
  .rich-text-content {
    word-wrap: break-word;
    line-height: 1.5;
  }
  .rich-text-content ul, 
  .rich-text-content ol {
    margin: 8px 0;
    padding-left: 20px;
  }
  .rich-text-content li {
    margin: 4px 0;
    line-height: 1.4;
  }
  .rich-text-content p {
    margin: 8px 0;
  }
  .rich-text-content h1, .rich-text-content h2, .rich-text-content h3 {
    margin: 12px 0 8px 0;
  }
  .rich-text-content strong {
    font-weight: bold;
  }
  .rich-text-content em {
    font-style: italic;
  }
  .rich-text-content br {
    display: block;
    margin: 4px 0;
  }
`;

// ===== Queries =====
const meQuery = {
  me: {
    resource: "me",
    params: {
      fields:
        "id,username,name,organisationUnits[id,name],userCredentials[userRoles[id,name]]",
    },
  },
};

const programOrgUnitsQuery = {
  program: {
    resource: `programs/${CONFIG.program}`,
    params: {
      fields: "organisationUnits[id]",
    },
  },
};


// ===== Component =====
function LeadershipManagementPage({ readOnlyReport = false }) {
  const engine = useDataEngine();
  const [competencies, setCompetencies] = useState([]);
  const [orgUnitId, setOrgUnitId] = useState(null);
  const [teiId, setTeiId] = useState(null);
  const [enrollmentId, setEnrollmentId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fiscalYear, setFiscalYear] = useState({ start: '', end: '' });
  const [fiscalYearLoaded, setFiscalYearLoaded] = useState(false);

  const [directReportPositions, setDirectReportPositions] = useState([]);
  const [reportingStructureLoaded, setReportingStructureLoaded] = useState(false);
  const [directReportAssessments, setDirectReportAssessments] = useState([]);
  const [staffList, setStaffList] = useState([]);

  const [currentUserPosition, setCurrentUserPosition] = useState(null);
  const [hasManager, setHasManager] = useState(false);
  const [expandedStaff, setExpandedStaff] = useState(null);
  const [topOrgUnit, setTopOrgUnit] = useState(null);
  const [savedIndicators, setSavedIndicators] = useState({});

  const [selectedFiscalYear, setSelectedFiscalYear] = useState(null);
  const [availableFiscalYears, setAvailableFiscalYears] = useState([]);
  const [showHistoricalView, setShowHistoricalView] = useState(false);

  const [templateCompetencies, setTemplateCompetencies] = useState([]); // The master template
  const [currentViewData, setCurrentViewData] = useState([]); // Current view (with ratings)
  const [isHistoricalMode, setIsHistoricalMode] = useState(false);

  const [viewMode, setViewMode] = useState('staff'); // 'staff' or 'manager_review'
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedManagerStaff, setExpandedManagerStaff] = useState(null);

  const [isLoadingAssessments, setIsLoadingAssessments] = useState(false);
  const [assessmentsLoaded, setAssessmentsLoaded] = useState(false);

  

  const [notification, setNotification] = useState({
    visible: false,
    type: 'success', // 'success', 'warning', 'critical'
    title: '',
    details: ''
  });


  const { data: userData, loading: userLoading, error: userError } = useDataQuery(meQuery);
  const { data: programData, loading: programLoading, error: programError } = useDataQuery(programOrgUnitsQuery);

  const isAdmin = React.useMemo(() => {
    // Multiple admin role IDs (Admin + CEO)
    const ADMIN_ROLE_IDS = ['R3lFolCMdoi', 'nLSSLch316I'];

    if (!userData?.me?.userCredentials?.userRoles) return false;

    return userData.me.userCredentials.userRoles.some(role =>
      ADMIN_ROLE_IDS.includes(role.id)
    );
  }, [userData]);

  // Instead of state, compute it reactively
const weightSetupComplete = React.useMemo(() => {
  if (!competencies.length) return false;
  
  const totalWeight = competencies.reduce((sum, c) => sum + (c.weight || 0), 0);
  const allHaveWeights = competencies.every(c => c.weight !== null && c.weight !== undefined);
  
  return allHaveWeights && Math.abs(totalWeight - 1) < 0.001;
}, [competencies]);


// Add this right after isAdmin
const isLevel5Org = React.useMemo(() => {
  if (!userData?.me?.organisationUnits?.[0]?.path) return false;
  const pathParts = userData.me.organisationUnits[0].path.split('/').filter(Boolean);
  // Level 5 orgs have path length of 5 (root + 4 levels)
  return pathParts.length === 5;
}, [userData]);


// ===== Unified Approval Feedback for Self-Assessment =====
    const unifiedFeedback = React.useMemo(() => {
     const approved = competencies.filter(c => c.approvalStatus === 'approved');
      const returned = competencies.filter(c => c.approvalStatus === 'returned');
      const pending = competencies.filter(c => !c.approvalStatus || c.approvalStatus === 'pending');

      if (competencies.length === 0) return null;

      if (pending.length === competencies.length) return null;

      if (approved.length === competencies.length) {
        const remark = approved.find(c => c.approvalRemark)?.approvalRemark || '';
        return { status: 'approved', remark };
      }
      if (returned.length === competencies.length) {
        const remark = returned.find(c => c.approvalRemark)?.approvalRemark || '';
        return { status: 'returned', remark };
      }

      return null;
    }, [competencies]);

  // ===== Financial Year Configuration =====
  

  const handleApproveAll = async (staffTei, staffName) => {
    const assessment = directReportAssessments.find(a => a.teiId === staffTei);
    if (!assessment) {
      console.error("❌ No assessment found for staff TEI:", staffTei);
      return;
    }

    const remark = window.prompt(`Enter remarks for approving all competencies for ${staffName}:`, "");
    if (remark === null) return;

    try {
      console.log("🔍 Starting bulk approval for:", { staffTei, staffName, competencyCount: assessment.competencies.length });

      // Ensure subordinate has an enrollment
      const subordinateEnrollmentId = await ensureEnrollment(staffTei, assessment.orgUnit || orgUnitId);

      // Process each competency
      for (const comp of assessment.competencies) {
        const eventData = {
          program: CONFIG.program,
          programStage: CONFIG.stages.approvals,
          trackedEntityInstance: staffTei,
          orgUnit: assessment.orgUnit || orgUnitId,
          enrollment: subordinateEnrollmentId,
          eventDate: new Date().toISOString().split('T')[0],
          status: "COMPLETED",
          dataValues: [
            { dataElement: CONFIG.de.competencyId, value: comp.competencyId },
            { dataElement: CONFIG.de.approvalStatus, value: 'approved' },
            { dataElement: CONFIG.de.approvalRemark, value: remark }
          ]
        };

        console.log("📤 Creating approval event for:", comp.name, "with payload:", eventData);

        const result = await engine.mutate({
          resource: "events",
          type: "create",
          data: eventData,
        });

        console.log("✅ Approval event created:", result);
      }

      // Update local state
      setDirectReportAssessments(prev =>
        prev.map(a =>
          a.teiId === staffTei
            ? {
              ...a,
              competencies: a.competencies.map(c => ({
                ...c,
                approvalStatus: 'approved',
                approvalRemark: remark
              }))
            }
            : a
        )
      );

      alert(`✅ All competencies approved successfully for ${staffName}!`);
    } catch (error) {
      console.error("❌ Bulk approval FAILED:", error);

      // 🔍 Detailed error inspection
      if (error.response?.importSummaries?.[0]) {
        const summary = error.response.importSummaries[0];
        console.error("Import Summary Details:", {
          status: summary.status,
          description: summary.description,
          conflicts: summary.conflicts,
          reference: summary.reference
        });

        // Check for specific conflict messages
        if (summary.description?.includes("ignored because not defined in program stage")) {
          const missingDe = summary.description.match(/Data Values \[(\w+)\] ignored/)?.[1];
          console.error("❗ Missing data element in Approvals stage:", missingDe);
          alert(`Configuration Error: Data element ${missingDe} is not in the Approvals stage. Please check program configuration.`);
        }
      }

      alert(`Failed to approve: ${error.message}`);
    }
  };

   useEffect(() => {
      // Add styles to head if not already there
        if (!document.getElementById('rich-text-styles')) {
          const style = document.createElement('style');
          style.id = 'rich-text-styles';
          style.textContent = richTextStyles;
          document.head.appendChild(style);
        }
    }, []);

  // Refresh manager view data when switching to manager_review tab
  useEffect(() => {
    // Only load if not already loaded and not currently loading
    if (viewMode === 'manager_review' && 
        (isAdmin || hasManager) && 
        staffList.length > 0 && 
        !isLoadingAssessments && 
        !assessmentsLoaded) {
      console.log('🔄 Loading manager view data...');
      loadAssessments();
    }
  }, [viewMode, staffList, isLoadingAssessments, assessmentsLoaded]);
  
  // Save manager's rating
  const handleManagerRatingSave = async (staffIndex, competencyIndex, period, data) => {
  const assessment = directReportAssessments[staffIndex];
  const staff = staffList.find(s => s.teiId === assessment.teiId);
  
  try {
    const subordinateEnrollmentId = await ensureEnrollment(assessment.teiId, assessment.orgUnit || orgUnitId);
    
    // Check if this competency is finalized - if yes, prevent saving
    const existingComp = assessment.competencies[competencyIndex];
    if (existingComp.finalApprovalStatus === 'finalized') {
      setNotification({
        visible: true,
        type: 'warning',
        title: 'Cannot Edit Finalized Review',
        details: 'This staff member has already finalized this competency. Please contact them to unfinalize if changes are needed.'
      });
      return;
    }
    
    // Try to find existing manager review event
    const existingEvents = await engine.query({
      events: {
        resource: 'events',
        params: {
          program: CONFIG.program,
          programStage: CONFIG.stages.managerReview,
          trackedEntityInstance: assessment.teiId,
          filter: [
            `${CONFIG.de.competencyId}:eq:${existingComp.competencyId}`,
            `${CONFIG.de.periodType}:eq:${period === 0 ? 'mid_year' : 'end_year'}`
          ].join(';'),
          fields: 'event,dataValues[dataElement,value]',
          pageSize: 1
        }
      }
    });
    
    const existingEvent = existingEvents.events?.events?.[0];
    
    const eventData = {
      program: CONFIG.program,
      programStage: CONFIG.stages.managerReview,
      trackedEntityInstance: assessment.teiId,
      orgUnit: assessment.orgUnit || orgUnitId,
      enrollment: subordinateEnrollmentId,
      eventDate: new Date().toISOString().split('T')[0],
      status: "COMPLETED",
      dataValues: [
        { dataElement: CONFIG.de.competencyId, value: assessment.competencies[competencyIndex].competencyId },
        { dataElement: CONFIG.de.managerRatingScore, value: data.rating.toString() },
        { dataElement: CONFIG.de.managerRatingComment, value: data.comment },
        { dataElement: CONFIG.de.periodType, value: period === 0 ? 'mid_year' : 'end_year' }
      ]
    };
    
    if (existingEvent) {
      // Update existing event
      await engine.mutate({
        resource: `events/${existingEvent.event}`,
        type: 'update',
        data: {
          ...eventData,
          event: existingEvent.event
        }
      });
      console.log('✅ Manager rating updated successfully for:', existingComp.name);
    } else {
      // Create new event
      await engine.mutate({
        resource: "events",
        type: "create",
        data: eventData,
      });
      console.log('✅ Manager rating created successfully for:', existingComp.name);
    }
    
    // Update local state
    setDirectReportAssessments(prev => {
      const newAssessments = [...prev];
      if (newAssessments[staffIndex]) {
        const updatedCompetencies = [...newAssessments[staffIndex].competencies];
        if (updatedCompetencies[competencyIndex]) {
          updatedCompetencies[competencyIndex] = {
            ...updatedCompetencies[competencyIndex],
            managerRatings: {
              ...(updatedCompetencies[competencyIndex].managerRatings || {}),
              [period]: data.rating
            },
            managerComments: {
              ...(updatedCompetencies[competencyIndex].managerComments || {}),
              [period]: data.comment
            }
          };
        }
        newAssessments[staffIndex] = {
          ...newAssessments[staffIndex],
          competencies: updatedCompetencies
        };
      }
      return newAssessments;
    });
    
    setNotification({
      visible: true,
      type: 'success',
      title: 'Manager rating saved successfully',
      details: `${assessment.competencies[competencyIndex].name}`
    });
    
  } catch (error) {
    console.error('Failed to save manager rating:', error);
    setNotification({
      visible: true,
      type: 'critical',
      title: 'Failed to save rating',
      details: error.message
    });
  }
};

    // Save staff finalization
  const handleStaffFinalization = async (competencyIndex, period, data) => {
    setIsSubmitting(true);
    try {
      const comp = competencies[competencyIndex];
      
      // FIRST: Delete any existing events with 'approved' status for this competency and period
      try {
        const existingEvents = await engine.query({
          events: {
            resource: 'events',
            params: {
              program: CONFIG.program,
              programStage: CONFIG.stages.staffFinalization,
              trackedEntityInstance: teiId,
              filter: `${CONFIG.de.competencyId}:eq:${comp.competencyId}`,
              fields: 'event,dataValues[dataElement,value]',
              pageSize: 20
            }
          }
        });
        
        const events = existingEvents.events?.events || [];
        for (const event of events) {
          const dataValuesMap = {};
          event.dataValues?.forEach(dv => {
            dataValuesMap[dv.dataElement] = dv.value;
          });
          const eventFinalStatus = dataValuesMap[CONFIG.de.finalApprovalStatus];
          const eventPeriod = dataValuesMap[CONFIG.de.periodType];
          const eventPeriodNum = eventPeriod === 'mid_year' ? 0 : 1;
          
          // Delete if it's 'approved' or if it's a duplicate for the same period
          if (eventFinalStatus === 'approved' || eventPeriodNum === period) {
            console.log(`🗑️ Deleting old event: ${event.event} with status: ${eventFinalStatus}`);
            await engine.mutate({
              resource: `events/${event.event}`,
              type: 'delete'
            });
          }
        }
      } catch (deleteError) {
        console.warn('Could not delete existing events:', deleteError);
      }
      
      // NOW create the new finalized event
      const eventData = {
        program: CONFIG.program,
        programStage: CONFIG.stages.staffFinalization,
        trackedEntityInstance: teiId,
        orgUnit: orgUnitId,
        enrollment: enrollmentId,
        eventDate: new Date().toISOString().split('T')[0],
        status: "COMPLETED",
        dataValues: [
          { dataElement: CONFIG.de.competencyId, value: comp.competencyId },
          { dataElement: CONFIG.de.staffAgreementStatus, value: data.agrees ? 'true' : 'false' },
          { dataElement: CONFIG.de.staffAgreementComment, value: data.comment },
          { dataElement: CONFIG.de.finalApprovalStatus, value: 'finalized' },
          { dataElement: CONFIG.de.periodType, value: period === 0 ? 'mid_year' : 'end_year' }
        ]
      };
      
      console.log('📤 Saving finalization:', eventData);
      
      await engine.mutate({
        resource: "events",
        type: "create",
        data: eventData,
      });
      
      console.log('✅ Finalization saved successfully');
      
      // Update local state
      setCompetencies(prev => {
        const newComps = [...prev];
        if (newComps[competencyIndex]) {
          newComps[competencyIndex] = {
            ...newComps[competencyIndex],
            staffAgreement: {
              ...(newComps[competencyIndex].staffAgreement || {}),
              [period]: data.agrees
            },
            staffAgreementComment: {
              ...(newComps[competencyIndex].staffAgreementComment || {}),
              [period]: data.comment
            },
            finalApprovalStatus: 'finalized'
          };
        }
        return newComps;
      });
      
      setNotification({
        visible: true,
        type: 'success',
        title: 'Feedback submitted successfully',
        details: data.agrees ? 'You agree with the assessment' : 'Your feedback has been recorded'
      });
      
      // Reload data
      setTimeout(async () => {
        await loadCurrentYearData();
      }, 500);
      
    } catch (error) {
      console.error('Failed to save finalization:', error);
      setNotification({
        visible: true,
        type: 'critical',
        title: 'Failed to submit feedback',
        details: error.message
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReturnAll = async (staffTei, staffName) => {
    const assessment = directReportAssessments.find(a => a.teiId === staffTei);
    if (!assessment) {
      console.error("❌ No assessment found for staff TEI:", staffTei);
      return;
    }

    const remark = window.prompt(`Enter remarks for returning all competencies for ${staffName}:`, "");
    if (remark === null) return;

    try {
      console.log("🔍 Starting bulk return for:", { staffTei, staffName, competencyCount: assessment.competencies.length });

      // Ensure subordinate has an enrollment
      const subordinateEnrollmentId = await ensureEnrollment(staffTei, assessment.orgUnit || orgUnitId);

      for (const comp of assessment.competencies) {
        const eventData = {
          program: CONFIG.program,
          programStage: CONFIG.stages.approvals,
          trackedEntityInstance: staffTei,
          orgUnit: assessment.orgUnit || orgUnitId,
          enrollment: subordinateEnrollmentId,
          eventDate: new Date().toISOString().split('T')[0],
          status: "COMPLETED",
          dataValues: [
            { dataElement: CONFIG.de.competencyId, value: comp.competencyId },
            { dataElement: CONFIG.de.approvalStatus, value: 'returned' },
            { dataElement: CONFIG.de.approvalRemark, value: remark }
          ]
        };

        console.log("📤 Creating return event for:", comp.name, "with payload:", eventData);

        const result = await engine.mutate({
          resource: "events",
          type: "create",
          data: eventData,
        });

        console.log("✅ Return event created:", result);
      }

      setDirectReportAssessments(prev =>
        prev.map(a =>
          a.teiId === staffTei
            ? {
              ...a,
              competencies: a.competencies.map(c => ({
                ...c,
                approvalStatus: 'returned',
                approvalRemark: remark
              }))
            }
            : a
        )
      );

      alert(`✅ All competencies returned for ${staffName}!`);
    } catch (error) {
      console.error("❌ Bulk return FAILED:", error);

      // 🔍 Same detailed inspection as above
      if (error.response?.importSummaries?.[0]) {
        const summary = error.response.importSummaries[0];
        console.error("Import Summary Details:", {
          status: summary.status,
          description: summary.description,
          conflicts: summary.conflicts
        });

        if (summary.description?.includes("ignored because not defined in program stage")) {
          const missingDe = summary.description.match(/Data Values \[(\w+)\] ignored/)?.[1];
          console.error("❗ Missing data element in Approvals stage:", missingDe);
          alert(`Configuration Error: Data element ${missingDe} is not in the Approvals stage.`);
        }
      }

      alert(`Failed to return: ${error.message}`);
    }
  };

const handleToggleStaff = async (index) => {
  const wasExpanded = expandedStaff === index;
  if (wasExpanded) {
    setExpandedStaff(null);
    return;
  }

  const assessment = directReportAssessments[index];
  if (!assessment) return;

  try {
    // ✅ Load config using subordinate's org unit
    const subordinateCompetencies = await loadCompetencyConfig(assessment.orgUnit);
    // ✅ Pass it to loadStaffAssessment
    const freshAssessment = await loadStaffAssessment(
      assessment.teiId,
      assessment.orgUnit,
      subordinateCompetencies // 👈 critical
    );

    if (freshAssessment) {
      setDirectReportAssessments(prev =>
        prev.map((a, i) => (i === index ? { ...a, ...freshAssessment } : a))
      );
    }
    setExpandedStaff(index);
  } catch (error) {
    console.error("Failed to reload staff assessment:", error);
    setExpandedStaff(index);
  }
};

  const checkIfUserHasManager = useCallback(async () => {
    if (!teiId) return false;

    try {
      // Get current user's position
      const currentPosition = await getCurrentUserPositionByTei(teiId);
      if (!currentPosition) {
        console.log("No position found for user");
        setHasManager(false);
        return false;
      }

      const positionAttrs = extractAttributes(currentPosition.attributes);
      const reportingToPositionTei = positionAttrs['SjP47qi3JeB']; // Reports to position attribute

      console.log("Current user position attributes:", {
        position: currentPosition.trackedEntityInstance,
        reportingTo: reportingToPositionTei
      });

      // If reportingToPositionTei exists and is not empty, user has a manager
      const hasMgr = !!(reportingToPositionTei && reportingToPositionTei.trim() !== '');
      setHasManager(hasMgr);

      console.log(`User ${hasMgr ? 'HAS' : 'DOES NOT HAVE'} a manager`);
      return hasMgr;

    } catch (error) {
      console.error("Error checking if user has manager:", error);
      setHasManager(false);
      return false;
    }
  }, [teiId]);


  // ===== Approval Handler =====
  const handleApproval = async (compIndex, status) => {
    const comp = competencies[compIndex];

    const remark = window.prompt(`Enter remarks for ${status}:`, comp.approvalRemark || '');
    if (remark === null) return;

    try {
      // Find existing weight event
      const weightEvent = await findWeightEventForCompetency(teiId, comp.name);

      if (weightEvent) {
        // Update existing event
        const updateData = {
          dataValues: [
            { dataElement: CONFIG.de.approvalStatus, value: status },
            { dataElement: CONFIG.de.approvalRemark, value: remark }
          ]
        };

        await engine.mutate({
          resource: `events/${weightEvent.event}`,
          type: 'update',
          data: updateData
        });
      } else {
        // Create new event if none exists
        const eventData = {
          program: CONFIG.program,
          programStage: CONFIG.stages.weights,
          trackedEntityInstance: teiId,
          orgUnit: orgUnitId,
          enrollment: enrollmentId,
          eventDate: new Date().toISOString().split('T')[0],
          status: "COMPLETED",
          dataValues: [
            { dataElement: CONFIG.de.competencyId, value: comp.competencyId },
            { dataElement: CONFIG.de.approvalStatus, value: status },
            { dataElement: CONFIG.de.approvalRemark, value: remark }
          ]
        };

        await engine.mutate({
          resource: "events",
          type: "create",
          data: eventData,
        });
      }

      // Update local state
      setCompetencies(prev => {
        const newComps = [...prev];
        newComps[compIndex] = {
          ...newComps[compIndex],
          approvalStatus: status,
          approvalRemark: remark
        };
        return newComps;
      });

      alert(`Competency ${status} successfully!`);
    } catch (error) {
      console.error("Approval error:", error);
      alert("Failed to update approval status.");
    }
  };

  const fiscalStart = new Date(fiscalYear.start);
  const fiscalEnd = new Date(fiscalYear.end);

  // ===== Loading States =====
  const [savingRatings, setSavingRatings] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const isRatingEditable = (comp, ratingIndex) => {
    // If no approval yet → editable during active period
    const status = comp.approvalStatus;
    if (!status || status === 'pending') {
      return ratingIndex === 0 ? isMidYearActive : isEndYearActive;
    }
    // If approved or returned → NOT editable
    return false;
  };

 const addNewCompetency = () => {
    setCompetencies(prev => [
      ...prev,
      {
        competencyId: null,
        tempId: Date.now() + Math.random(),
        name: '',
        definition: '',
        eventId: null,
        weight: null,
        weightLocked: false,
        ratings: [null, null], // Staff self-ratings
        managerRatings: [null, null], // Manager's ratings
        comments: ["", ""],
        managerComments: ["", ""], // Manager's comments
        staffAgreement: [null, null], // Staff agreement status (true/false)
        staffAgreementComment: ["", ""], // Staff comments on manager rating
        managerReviewStatus: 'pending', // pending, reviewed
        finalApprovalStatus: null, // null or 'finalized'
        isSubmittedMidYear: false,    
        isSubmittedEndYear: false,    
      }
    ]);
  };

  // Find the top-most org unit (level 5) that has the program assigned
  const getTopOrgUnit = (userOrgUnits, programOrgUnits) => {
    if (!Array.isArray(userOrgUnits) || !Array.isArray(programOrgUnits)) {
      console.warn("Invalid org unit data:", { userOrgUnits, programOrgUnits });
      return null;
    }
    // Get intersection of user's org units and program org units
    const validOrgUnits = userOrgUnits.filter(ou =>
      programOrgUnits.some(pou => pou.id === ou.id)
    );

    if (validOrgUnits.length === 0) return null;


    return validOrgUnits[0].id;
  };

 

  const updateCompetency = (index, field, value) => {
    setCompetencies(prev => {
      const newComps = [...prev];
      if (field === 'name') {
        // Allow spaces, convert to uppercase
        newComps[index] = {
          ...newComps[index],
          name: value.toUpperCase() // ← NO .trim() during typing
        };
      } else if (field === 'definition') {
        // ✅ ALLOW ANY INPUT - NO TRIMMING DURING TYPING
        newComps[index] = {
          ...newComps[index],
          definition: value // ← RAW INPUT
        };
      } else {
        newComps[index] = { ...newComps[index], [field]: value };
      }
      return newComps;
    });
  };

  // ✅ CORRECT: Only blocks deletion if NON-CONFIG events exist
  const checkCompetencyHasRealData = async (comp) => {
  // 1. Check local state first
  if (comp.ratings.some(r => r != null)) return true;

  // 2. Check backend for real assessment events (exclude CONFIG.stages.config)
  try {
    const nonConfigStages = [
      CONFIG.stages.midYear,
      CONFIG.stages.endYear,
      CONFIG.stages.approvals
    ].join(';');

    const query = {
      events: {
        resource: 'events',
        params: {
          program: CONFIG.program,
          programStage: nonConfigStages,
          trackedEntityInstance: teiId, 
          fields: 'event',
          filter: `${CONFIG.de.competencyCode}:eq:${encodeURIComponent(comp.name)}`, 
          pageSize: 1
        }
      }
    };

    const result = await engine.query(query);
    return (result.events?.events?.length || 0) > 0;
  } catch (error) {
    console.warn("Error checking competency real data:", error);
    return false; // Allow deletion if check fails
  }
};

  // Check if a competency has any ratings in the system
const checkCompetencyInUse = async (competencyName) => {
  if (!competencyName?.trim()) return false;

  try {
    // Search across all assessment-related stages
    const stages = [
      CONFIG.stages.midYear,
      CONFIG.stages.endYear,
      CONFIG.stages.approvals
    ].join(';');

    const query = {
      events: {
        resource: 'events',
        params: {
          program: CONFIG.program,
          programStage: stages,
          fields: 'event',
          filter: `${CONFIG.de.competencyCode}:eq:${encodeURIComponent(competencyName)}`,
          pageSize: 1 // We only need to know if >=1 exists
        }
      }
    };

    const result = await engine.query(query);
    const totalCount = result.events?.pager?.total || 0;
    return totalCount > 0;
  } catch (error) {
    console.error("Error checking if competency is in use:", error);
    //  On error, assume it's in use to prevent accidental deletion
    return true;
  }
};

const deleteCompetency = async (index) => {
  const comp = competencies[index];
  if (!comp.name?.trim()) {
    // Allow deletion of empty/new rows without confirmation
    setCompetencies(prev => prev.filter((_, i) => i !== index));
    return;
  }

  if (!window.confirm(`Are you sure you want to delete competency "${comp.name}"? This cannot be undone.`)) {
    return;
  }

  // Delete from database if it's a saved config event
  if (comp.eventId) {
    try {
      await engine.mutate({
        resource: `events/${comp.eventId}`,
        type: 'delete'
      });
      console.log(`✅ Deleted config event: ${comp.eventId}`);
    } catch (error) {
      console.warn("Failed to delete config event:", error);
      // Still proceed with UI removal
    }
  }

  // Remove from local state
  setCompetencies(prev => prev.filter((_, i) => i !== index));
  alert(`✅ Competency "${comp.name}" deleted successfully.`);
};

  // Handle drag start
const handleDragStart = (e, index) => {
  e.dataTransfer.setData('text/plain', index.toString());
  //e.dataTransfer.effectAllowed = 'move';
};

// Handle drag over (required for drop)
const handleDragOver = (e) => {
  e.preventDefault();
  e.dataTransfer.dropEffect = 'move';
};

    // Handle drop
    const handleDrop = async (e, targetIndex) => {
      e.preventDefault();
      const sourceIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
      if (sourceIndex === targetIndex || isNaN(sourceIndex)) return;

      // Reorder local state
      setCompetencies(prev => {
        const newComps = [...prev];
        const [moved] = newComps.splice(sourceIndex, 1);
        newComps.splice(targetIndex, 0, moved);
        return newComps;
      });

      // ✅ Auto-save after drop
      await saveCompetencyConfig();
    };

const saveCompetencyConfig = async () => {
  if (!topOrgUnit) {
    alert("No valid top-level org unit found");
    return;
  }

  try {
    // 1. Load current config ONLY to get existing event IDs (do not use for order!)
    const currentConfig = await loadCompetencyConfig(topOrgUnit);
    const configMap = new Map();
    currentConfig.forEach(comp => {
      configMap.set(comp.name, comp);
    });

    // 2. Save each competency in CURRENT UI ORDER
    const updatedCompetencies = [];
    for (const comp of competencies) {
      const name = comp.name.trim();
      if (!name) continue;

      const existingComp = configMap.get(name);
      const dataValues = [
        { dataElement: CONFIG.de.competencyCode, value: name },
        { dataElement: CONFIG.de.competencyDefinition, value: comp.definition.trim() || name }
      ];
      if (comp.weight != null) {
        dataValues.push({ dataElement: CONFIG.de.weightValue, value: comp.weight.toString() });
      }

      let eventId = comp.eventId;
      if (existingComp && existingComp.eventId) {
        // Update existing
        await engine.mutate({
          resource: `events/${existingComp.eventId}`,
          type: 'update',
          data: {
            event: existingComp.eventId,
            program: CONFIG.program,
            programStage: CONFIG.stages.config,
            orgUnit: topOrgUnit,
            trackedEntityInstance: teiId,
            enrollment: enrollmentId,
            eventDate: new Date().toISOString().split('T')[0],
            status: 'COMPLETED',
            dataValues
          }
        });
        eventId = existingComp.eventId;
      } else {
        // Create new
        const result = await engine.mutate({
          resource: 'events',
          type: 'create',
          data: {
            program: CONFIG.program,
            programStage: CONFIG.stages.config,
            orgUnit: topOrgUnit,
            trackedEntityInstance: teiId,
            enrollment: enrollmentId,
            eventDate: new Date().toISOString().split('T')[0],
            status: 'COMPLETED',
            dataValues
          }
        });
        eventId = result.response?.reference || result.response?.uid;
      }

      // Build updated comp with correct ID
      updatedCompetencies.push({
        ...comp,
        eventId,
        competencyId: eventId
      });
    }

     // Instead, update state with the SAVED version (preserves drag order)
    setCompetencies(updatedCompetencies);

    alert("✅ Competency configuration saved successfully!");
  } catch (error) {
    console.error("Failed to save competency config:", error);
    alert("Failed to save. Check console for details.");
  }
};

  // ===== Helper Functions =====
  const calculateScore = (comp) => {
    // Include all non-null ratings, regardless of locked status
    const filled = comp.ratings.filter(r => r != null);
    if (!filled.length) return 0;
    const avg = filled.reduce((a, b) => a + b, 0) / filled.length;
    return (comp.weight || 0) * avg;
  };

  // Helper: Extract attributes into a map (same as ObjectivesList.jsx)
  const extractAttributes = (attributes = []) => {
    const map = {};
    attributes?.forEach((attr) => {
      map[attr.attribute] = attr.value;
    });
    return map;
  };

  // Get direct reports function (similar to ObjectivesList.jsx)
  const getDirectReports = async (position) => {
    if (!position) return [];

    try {
      const query = {
        positions: {
          resource: 'trackedEntityInstances',
          params: {
            trackedEntityType: 'UGGo0Ebjl0E',
            ouMode: 'ACCESSIBLE',
            filter: `SjP47qi3JeB:eq:${position.trackedEntityInstance}`,
            fields: 'trackedEntityInstance,orgUnit,attributes[attribute,value]',
            pageSize: 100,
          },
        },
      };

      const result = await engine.query(query);
      const directReportPositions = result.positions?.trackedEntityInstances || [];

      console.log(`📋 Found ${directReportPositions.length} direct report positions`);
      return directReportPositions;

    } catch (error) {
      console.error('Error fetching direct reports:', error);
      return [];
    }
  };

  // ✅ CORRECT: Find position by Person TEI (not user ID)
  const getCurrentUserPositionByTei = async (personTei) => {
    if (!personTei) return null;
    try {
      const query = {
        positions: {
          resource: 'trackedEntityInstances',
          params: {
            trackedEntityType: 'UGGo0Ebjl0E',
            ouMode: 'ACCESSIBLE',
            filter: `V66IFsw5kq6:eq:${personTei}`,
            fields: 'trackedEntityInstance,attributes[attribute,value]',
            pageSize: 1
          }
        }
      };
      const result = await engine.query(query);
      const positions = result.positions?.trackedEntityInstances || [];
      if (positions.length > 0) {
        console.log(`✅ Found position for TEI ${personTei}:`, positions[0].trackedEntityInstance);
        return positions[0];
      }
      console.log(`❌ No position found for TEI ${personTei}`);
      return null;
    } catch (error) {
      console.error('Error getting current user position by TEI:', error);
      return null;
    }
  };

  const calculateFinalScore = () =>
    competencies.reduce((sum, c) => sum + calculateScore(c), 0).toFixed(2);


  const WeightInput = ({ weight, onChange, disabled }) => {
    const [inputValue, setInputValue] = useState("");
    const [isEditing, setIsEditing] = useState(false);
    const inputRef = useRef();

    // In WeightInput component
    useEffect(() => {
      if (weight !== null && weight !== undefined) {
        setInputValue(Math.round(weight * 100).toString());
      } else {
        setInputValue("");
      }
    }, [weight]);

      
    const handleChange = (value) => {
      // Only allow digits
      const digitsOnly = value.replace(/\D/g, '');
      setInputValue(digitsOnly);
    };

    const handleFocus = () => {
      setIsEditing(true);
    };

    const handleBlur = () => {
      setIsEditing(false);

      if (inputValue === "" || inputValue === "0") { 
      return;
      }

      const num = parseInt(inputValue, 10);
      if (isNaN(num)) {
        setInputValue("");
        onChange(null);
        return;
      }

      // Clamp to 0-100
      const clamped = Math.max(0, Math.min(100, num));
      setInputValue(clamped.toString());
      onChange(clamped / 100);
    };

    const handleKeyDown = (e) => {
      // Submit on Enter key
      if (e.key === 'Enter') {
        inputRef.current.blur();
      }
    };

    return (
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={inputValue}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        style={{
          width: '70px',
          padding: '8px 6px',
          border: '1px solid #a0adba',
          borderRadius: '3px',
          fontSize: '14px',
          textAlign: 'center',
          backgroundColor: disabled ? '#f5f5f5' : 'white',
          color: disabled ? '#666' : 'inherit',
        }}
      />
    );
  };


  // Add this function to load manager ratings separately
  const loadManagerRatingsOnly = async () => {
    if (!teiId || !orgUnitId) return {};
    
    try {
      const query = {
        events: {
          resource: 'events',
          params: {
            program: CONFIG.program,
            programStage: CONFIG.stages.managerReview,
            trackedEntityInstance: teiId,
            fields: 'event,eventDate,dataValues[dataElement,value]',
            pageSize: 500
          }
        }
      };
      
      const result = await engine.query(query);
      const events = result.events?.events || [];
      
      const managerRatingsMap = {};
      
      events.forEach(event => {
        const dataValuesMap = {};
        event.dataValues?.forEach(dv => {
          dataValuesMap[dv.dataElement] = dv.value;
        });
        
        const competencyId = dataValuesMap[CONFIG.de.competencyId];
        const periodType = dataValuesMap[CONFIG.de.periodType];
        const period = periodType === 'mid_year' ? 0 : 1;
        const rating = dataValuesMap[CONFIG.de.managerRatingScore];
        const comment = dataValuesMap[CONFIG.de.managerRatingComment];
        
        if (competencyId && rating) {
          if (!managerRatingsMap[competencyId]) {
            managerRatingsMap[competencyId] = { ratings: [null, null], comments: ["", ""] };
          }
          managerRatingsMap[competencyId].ratings[period] = parseInt(rating);
          managerRatingsMap[competencyId].comments[period] = comment || '';
        }
      });
      
      return managerRatingsMap;
    } catch (error) {
      console.error('Error loading manager ratings:', error);
      return {};
    }
  };

  const saveCommentForCompetency = async (comp, ratingIndex, comment) => {
    const ratingValue = comp.ratings[ratingIndex] || 0;
    await saveRatingForCompetency(comp, ratingIndex, ratingValue, comment);
  };

 
  const loadCompetencyConfig = useCallback(async (ouId) => {
      try {
        const params = {
          program: CONFIG.program,
          programStage: CONFIG.stages.config,
          ouMode: 'ACCESSIBLE',
          fields: 'event,eventDate,dataValues[dataElement,value]',
          pageSize: 50,
        };
        
        
        const query = { configEvents: { resource: 'events', params } };
        const result = await engine.query(query);
        const events = result.configEvents?.events || [];
        
        return events.map(event => {
          const codeDV = event.dataValues.find(dv => dv.dataElement === CONFIG.de.competencyCode);
          const defDV = event.dataValues.find(dv => dv.dataElement === CONFIG.de.competencyDefinition);
          const weightDV = event.dataValues.find(dv => dv.dataElement === CONFIG.de.weightValue);
          return {
            competencyId: event.event,
            name: codeDV?.value || '',
            definition: defDV?.value || codeDV?.value || '',
            eventId: event.event,
            weight: weightDV ? parseFloat(weightDV.value) : null,
            weightLocked: !!weightDV,
            ratings: [null, null],
            ratingsLocked: [false, false],
            comments: ["", ""],
            approvalStatus: 'pending',
            approvalRemark: ''
          };
        }).filter(c => c.name.trim());
      } catch (error) {
        console.warn("Competency config load failed:", error.message);
        return [];
      }
   }, [engine]); 

   

  // Function to load ratings only (not competencies) for a specific fiscal year
  async function loadRatingsForFiscalYear(fiscalYearConfig) {
    if (!teiId || !orgUnitId || templateCompetencies.length === 0) return null;
    
    try {
      const query = {
        events: {
          resource: 'events',
          params: {
            program: CONFIG.program,
            trackedEntityInstance: teiId,
            fields: 'event,programStage,eventDate,dataValues[dataElement,value]',
            pageSize: 500
          }
        }
      };
      
      const result = await engine.query(query);
      const events = result.events?.events || [];
      
      // Filter events by date client-side
      const filteredEvents = events.filter(event => {
        if (!event.eventDate) return false;
        const eventDate = event.eventDate.split('T')[0];
        return eventDate >= fiscalYearConfig.start && eventDate <= fiscalYearConfig.end;
      });
      
      // Map ratings to template competencies using filtered events
      const competenciesWithRatings = templateCompetencies.map(comp => {
        const compData = {
          ...comp,
          ratings: [null, null],
          managerRatings: [null, null],
          comments: ["", ""],
          managerComments: ["", ""],
          staffAgreement: [null, null],
          staffAgreementComment: ["", ""],
          finalApprovalStatus: null,
          isSubmittedMidYear: false,
          isSubmittedEndYear: false
        };
        
        filteredEvents.forEach(event => {
          const dataValuesMap = {};
          event.dataValues?.forEach(dv => {
            dataValuesMap[dv.dataElement] = dv.value;
          });
          
          const idDV = dataValuesMap[CONFIG.de.competencyId];
          if (idDV !== comp.competencyId) return;
          
          switch (event.programStage) {
            // REMOVED: case CONFIG.stages.approvals - no longer needed
            
            case CONFIG.stages.managerReview:
              const managerRatingDV = dataValuesMap[CONFIG.de.managerRatingScore];
              const managerCommentDV = dataValuesMap[CONFIG.de.managerRatingComment];
              const managerPeriodDV = dataValuesMap[CONFIG.de.periodType];
              const managerPeriod = managerPeriodDV === 'mid_year' ? 0 : 1;
              
              if (managerRatingDV) {
                compData.managerRatings[managerPeriod] = parseInt(managerRatingDV);
              }
              if (managerCommentDV) {
                compData.managerComments[managerPeriod] = managerCommentDV;
              }
              break;
            
            case CONFIG.stages.staffFinalization:
            const agreementDV = dataValuesMap[CONFIG.de.staffAgreementStatus];
            const agreementCommentDV = dataValuesMap[CONFIG.de.staffAgreementComment];
            const finalStatusDV = dataValuesMap[CONFIG.de.finalApprovalStatus];
            const periodTypeDV = dataValuesMap[CONFIG.de.periodType];
            const staffPeriod = periodTypeDV === 'mid_year' ? 0 : 1;
            
            console.log('📝 Found staffFinalization event:', {
              competencyId: idDV,
              period: staffPeriod,
              agreement: agreementDV,
              comment: agreementCommentDV,
              finalStatus: finalStatusDV
            });
            
            if (agreementDV) {
              // ONLY accept 'finalized' status - ignore 'approved' from old system
              if (finalStatusDV === 'finalized') {
                compData.staffAgreement[staffPeriod] = agreementDV === 'true';
                compData.staffAgreementComment[staffPeriod] = agreementCommentDV || '';
                compData.finalApprovalStatus = 'finalized';
                console.log(`   ✅ Setting finalApprovalStatus to: finalized for ${comp.name}`);
              } else if (!compData.finalApprovalStatus && finalStatusDV === 'finalized') {
                // Fallback - only set if not already finalized
                compData.staffAgreement[staffPeriod] = agreementDV === 'true';
                compData.staffAgreementComment[staffPeriod] = agreementCommentDV || '';
                compData.finalApprovalStatus = 'finalized';
              } else {
                console.log(`   ⚠️ Skipping event with status: ${finalStatusDV} (not 'finalized')`);
              }
            }
            break;
            
            case CONFIG.stages.midYear:
              const midScoreDV = dataValuesMap[CONFIG.de.ratingScore];
              const midCommentDV = dataValuesMap[CONFIG.de.ratingComment];
              if (midScoreDV) {
                compData.ratings[0] = parseInt(midScoreDV);
                compData.isSubmittedMidYear = true;
              }
              if (midCommentDV) compData.comments[0] = midCommentDV;
              break;

            case CONFIG.stages.endYear:
              const endScoreDV = dataValuesMap[CONFIG.de.ratingScore];
              const endCommentDV = dataValuesMap[CONFIG.de.ratingComment];
              if (endScoreDV) {
                compData.ratings[1] = parseInt(endScoreDV);
                compData.isSubmittedEndYear = true;
              }
              if (endCommentDV) compData.comments[1] = endCommentDV;
              break;
          }
        });
        
        return compData;
      });
      
      return competenciesWithRatings;
    } catch (error) {
      console.error('Error loading ratings:', error);
      return null;
    }
  }

  // Function to load current fiscal year data
  const loadCurrentYearData = useCallback(async () => {
    if (!teiId || !orgUnitId || templateCompetencies.length === 0) return;
    
    setLoading(true);
    try {
      const currentFiscalConfig = {
        start: fiscalYear.start,
        end: fiscalYear.end
      };
      
      // Load both ratings and manager ratings
      const ratingsData = await loadRatingsForFiscalYear(currentFiscalConfig);
      const managerRatingsData = await loadManagerRatingsOnly();
      
      console.log('📊 Loaded data:', {
        ratingsDataLength: ratingsData?.length,
        managerRatingsDataKeys: Object.keys(managerRatingsData).length,
        // Log the first competency's finalApprovalStatus if available
        firstCompFinalStatus: ratingsData?.[0]?.finalApprovalStatus
      });
      
      if (ratingsData && ratingsData.length > 0) {
        // Merge manager ratings into the competencies but PRESERVE all existing fields
        const mergedData = ratingsData.map(comp => {
          const managerData = managerRatingsData[comp.competencyId];
          
          // Log each competency's finalApprovalStatus for debugging
          if (comp.finalApprovalStatus === 'finalized') {
            console.log(`✅ Competency ${comp.name} has finalApprovalStatus = finalized`);
          }
          
          if (managerData) {
            return {
              ...comp,  // This preserves ratings, comments, staffAgreement, finalApprovalStatus, etc.
              managerRatings: managerData.ratings,
              managerComments: managerData.comments
            };
          }
          return comp;  // This preserves all fields including finalApprovalStatus
        });
        
        console.log('Final merged data - first competency finalApprovalStatus:', mergedData[0]?.finalApprovalStatus);
        
        setCompetencies(mergedData);
        setCurrentViewData(mergedData);
      } else {
        // Only use empty template if no ratings data exists
        const emptyRatingsData = templateCompetencies.map(comp => {
          const managerData = managerRatingsData[comp.competencyId];
          return {
            ...comp,
            ratings: [null, null],
            comments: ["", ""],
            ratingsLocked: [false, false],
            managerRatings: managerData ? managerData.ratings : [null, null],
            managerComments: managerData ? managerData.comments : ["", ""],
            staffAgreement: [null, null],
            staffAgreementComment: ["", ""],
            finalApprovalStatus: null,
            isSubmittedMidYear: false,
            isSubmittedEndYear: false
          };
        });
        setCompetencies(emptyRatingsData);
        setCurrentViewData(emptyRatingsData);
      }
      } catch (error) {
        console.error('Failed to load current year data:', error);
      } finally {
        setLoading(false);
      }
    }, [teiId, orgUnitId, templateCompetencies, fiscalYear, loadRatingsForFiscalYear]);

 const loadHistoricalAssessments = async (year) => {
      setLoading(true);
      try {
        const fiscalConfig = availableFiscalYears.find(fy => fy.year === year);
        if (!fiscalConfig) return;
        
        // Load ratings for this fiscal year
        const ratingsData = await loadRatingsForFiscalYear(fiscalConfig);
        
        if (ratingsData) {
          setCompetencies(ratingsData);
          setCurrentViewData(ratingsData);
          setShowHistoricalView(true);
          setSelectedFiscalYear(year);
        } else {
          // If no ratings, show template competencies with empty ratings
          const emptyRatingsData = templateCompetencies.map(comp => ({
            ...comp,
            ratings: [null, null],
            comments: ["", ""],
            ratingsLocked: [false, false],
            approvalStatus: 'pending',
            approvalRemark: ''
          }));
          setCompetencies(emptyRatingsData);
          setCurrentViewData(emptyRatingsData);
          setShowHistoricalView(true);
          setSelectedFiscalYear(year);
        }
      } catch (error) {
        console.error('Failed to load historical assessments:', error);
        setError('Could not load historical data');
      } finally {
        setLoading(false);
      }
  };

  // Function to load current fiscal year data

  const deleteApprovalEvent = async (comp, staffTei) => {
      try {
        // Find existing approval event
        const query = {
          events: {
            resource: 'events',
            params: {
              program: CONFIG.program,
              programStage: CONFIG.stages.approvals,
              trackedEntityInstance: staffTei,
              filter: `${CONFIG.de.competencyId}:eq:${comp.competencyId}`,
              fields: 'event',
              pageSize: 1
            }
          }
        };
        const result = await engine.query(query);
        const events = result.events?.events || [];
        if (events.length > 0) {
          await engine.mutate({
            resource: `events/${events[0].event}`,
            type: 'delete'
          });
          console.log(`✅ Deleted approval event for ${comp.name}`);
        }
      } catch (error) {
        console.warn("Failed to delete approval event:", error);
      }
   };

   const handleCompetencyEdit = async (compIndex) => {
      const comp = competencies[compIndex];
      if (comp.approvalStatus === 'returned') {
        
        await deleteApprovalEvent(comp, teiId);
        // Update local state
        setCompetencies(prev => {
          const newComps = [...prev];
          newComps[compIndex] = {
            ...newComps[compIndex],
            approvalStatus: 'pending',
            approvalRemark: ''
          };
          return newComps;
        });
      }
    };


const loadStaffAssessment = async (staffTei, staffOrgUnit, globalCompetencies = []) => {
  try {
    console.log(`📥 Loading assessment for staff TEI: ${staffTei}`);

    if (!globalCompetencies?.length) {
      console.log(`No competencies configured for orgUnit ${staffOrgUnit}`);
      return null;
    }

    const query = {
      events: {
        resource: 'events',
        params: {
          program: CONFIG.program,
          trackedEntityInstance: staffTei,
          fields: 'event,programStage,eventDate,dataValues[dataElement,value]',
          pageSize: 200
        }
      }
    };

    const result = await engine.query(query);
    const events = result.events?.events || [];

    const processedComps = globalCompetencies.map(comp => {
      const compData = {
        ...comp,
        weight: comp.weight,
        weightLocked: true,
        ratings: [null, null],
        managerRatings: [null, null],  // Ensure this is initialized
        comments: ["", ""],
        managerComments: ["", ""],     // Ensure this is initialized
        ratingsLocked: [false, false],
        approvalStatus: 'pending',
        approvalRemark: ''
      };

      events.forEach(event => {
        const idDV = event.dataValues?.find(dv => dv.dataElement === CONFIG.de.competencyId);
        if (idDV?.value !== comp.competencyId) return;

        switch (event.programStage) {
          case CONFIG.stages.managerReview:  // Add this case
            const managerRatingDV = event.dataValues?.find(dv => dv.dataElement === CONFIG.de.managerRatingScore);
            const managerCommentDV = event.dataValues?.find(dv => dv.dataElement === CONFIG.de.managerRatingComment);
            const periodTypeDV = event.dataValues?.find(dv => dv.dataElement === CONFIG.de.periodType);
            const managerPeriod = periodTypeDV?.value === 'mid_year' ? 0 : 1;
            
            if (managerRatingDV) {
              compData.managerRatings[managerPeriod] = parseInt(managerRatingDV.value);
            }
            if (managerCommentDV) {
              compData.managerComments[managerPeriod] = managerCommentDV.value;
            }
            break;
            
          case CONFIG.stages.midYear:
            const midScoreDV = event.dataValues?.find(dv => dv.dataElement === CONFIG.de.ratingScore);
            const midCommentDV = event.dataValues?.find(dv => dv.dataElement === CONFIG.de.ratingComment);
            if (midScoreDV) {
              compData.ratings[0] = parseInt(midScoreDV.value);
              compData.ratingsLocked[0] = true;
            }
            if (midCommentDV) compData.comments[0] = midCommentDV.value;
            break;

          case CONFIG.stages.endYear:
            const endScoreDV = event.dataValues?.find(dv => dv.dataElement === CONFIG.de.ratingScore);
            const endCommentDV = event.dataValues?.find(dv => dv.dataElement === CONFIG.de.ratingComment);
            if (endScoreDV) {
              compData.ratings[1] = parseInt(endScoreDV.value);
              compData.ratingsLocked[1] = true;
            }
            if (endCommentDV) compData.comments[1] = endCommentDV.value;
            break;
        }
      });

      return compData;
    });

    const hasData = processedComps.some(comp =>
      comp.ratings.some(r => r !== null) ||
      comp.managerRatings.some(r => r !== null) ||
      comp.approvalStatus !== 'pending'
    );

    console.log(`Assessment data ${hasData ? 'found' : 'not found'} for staff ${staffTei}`);
    console.log('Manager ratings loaded:', processedComps.map(c => ({ name: c.name, managerRatings: c.managerRatings })));

    return {
      competencies: processedComps,
      teiId: staffTei,
      orgUnit: staffOrgUnit,
      hasData: hasData
    };
  } catch (error) {
    console.error(`Error loading staff assessment:`, error);
    return null;
  }
};
  // Track original ratings to detect changes
const originalRatingsRef = useRef([]);

// After loading competencies, save original state
useEffect(() => {
  originalRatingsRef.current = competencies.map(c => [...c.ratings]);
}, [competencies]);

useEffect(() => {
  const loadAvailableFiscalYears = async () => {
    if (!engine || !teiId) return;
    
    try {
      // Query all assessment events to get distinct fiscal years
      const response = await engine.query({
        events: {
          resource: 'events',
          params: {
            program: CONFIG.program,
            trackedEntityInstance: teiId,
            fields: 'eventDate',
            pageSize: 1000,
            ouMode: 'ACCESSIBLE'
          }
        }
      });
      
      const events = response.events?.events || [];
      
      // Extract unique years from event dates
      const uniqueYears = new Set();
      events.forEach(event => {
        if (event.eventDate) {
          const eventYear = new Date(event.eventDate).getFullYear();
          uniqueYears.add(eventYear);
        }
      });
      
      // Also include current fiscal year even if no data
      const now = new Date();
      const currentYear = now.getFullYear();
      const julyFirstCurrentYear = new Date(currentYear, 6, 1);
      let currentFiscalStartYear = now >= julyFirstCurrentYear ? currentYear : currentYear - 1;
      uniqueYears.add(currentFiscalStartYear);
      
      // Sort years in descending order
      const sortedYears = Array.from(uniqueYears).sort((a, b) => b - a);
      
      // Build fiscal year list
      const fiscalYearsList = sortedYears.map(year => ({
        year: year,
        start: `${year}-07-01`,
        end: `${year + 1}-06-30`,
        label: `${year} - ${year + 1}`,
        isCurrent: year === currentFiscalStartYear
      }));
      
      setAvailableFiscalYears(fiscalYearsList);
      console.log('✅ Generated fiscal years from database:', fiscalYearsList);
      
    } catch (error) {
      console.error('Failed to load fiscal years:', error);
      setAvailableFiscalYears([]);
    }
  };
  
  if (teiId) {
    loadAvailableFiscalYears();
  }
}, [engine, teiId]);

const handleSaveRating = async (compIndex, ratingIndex) => {
  const comp = competencies[compIndex];
  const ratingValue = comp.ratings[ratingIndex];

  if (ratingValue === null || ratingValue === undefined) {
    alert("Please select a rating before saving.");
    return;
  }

  if (!comp.comments[ratingIndex]?.trim()) {
    const comment = window.prompt(
      `Please add a comment for "${comp.name}" (${ratingIndex === 0 ? 'Mid-Year' : 'End-Year'}):`,
      ""
    );
    
    if (comment === null) return;
    
    if (!comment?.trim()) {
      alert("Comment cannot be empty.");
      return;
    }

    setCompetencies(prev => {
      const newComps = [...prev];
      newComps[compIndex].comments[ratingIndex] = comment.trim();
      return newComps;
    });
  }

  const programStage = ratingIndex === 0 ? CONFIG.stages.midYear : CONFIG.stages.endYear;
  
  const dataValues = [
    { dataElement: CONFIG.de.competencyId, value: comp.competencyId },
    { dataElement: CONFIG.de.ratingScore, value: ratingValue.toString() },
    { dataElement: CONFIG.de.ratingComment, value: comp.comments[ratingIndex] }
  ];

  try {
    await saveCompetencyEvent(comp, programStage, dataValues);

    const indicatorKey = `${compIndex}-${ratingIndex}`;
    setSavedIndicators(prev => ({ 
      ...prev, 
      [indicatorKey]: { 
        show: true,
        timestamp: Date.now()
      } 
    }));
    
    // Set period-specific submission flag
    setCompetencies(prev => {
      const newComps = [...prev];
      if (ratingIndex === 0) {
        newComps[compIndex] = {
          ...newComps[compIndex],
          isSubmittedMidYear: true,
        };
      } else {
        newComps[compIndex] = {
          ...newComps[compIndex],
          isSubmittedEndYear: true,
        };
      }
      return newComps;
    });
    
    setNotification({
      visible: true,
      type: 'success',
      title: 'Rating saved successfully',
      details: `${comp.name} - ${ratingIndex === 0 ? 'Mid-Year' : 'End-Year'}`
    });

  } catch (error) {
    console.error("Failed to save rating:", error);
    setNotification({
      visible: true,
      type: 'critical',
      title: 'Failed to save rating',
      details: error.message || 'Please try again.'
    });
  }
};;

 useEffect(() => {
  if (userData?.me?.organisationUnits?.length > 0 && programData?.program?.organisationUnits) {
    const userOrgUnits = userData.me.organisationUnits.map(ou => ou.id);
    const programOrgUnits = programData.program.organisationUnits.map(ou => ou.id);
    const validOrgUnits = userOrgUnits.filter(ou => programOrgUnits.includes(ou));
    
    if (validOrgUnits.length > 0) {
      const computedTopOrgUnit = getTopOrgUnit(userData.me.organisationUnits, programData.program.organisationUnits);
      setTopOrgUnit(computedTopOrgUnit);
      
      // Load competencies ONCE as template
      loadCompetencyConfig(computedTopOrgUnit).then(dynamicComps => {
        console.log('✅ Template Competencies loaded:', dynamicComps?.length || 0);
        
        // Store as template (never changes)
        setTemplateCompetencies(dynamicComps || []);
        
        // Also set as current view
        setCompetencies(dynamicComps || []);
        setCurrentViewData(dynamicComps || []);
      }).catch(error => {
        console.error("Competency config load failed:", error);
        setTemplateCompetencies([]);
        setCompetencies([]);
        setCurrentViewData([]);
      });
      
      const selectedOrgUnitId = validOrgUnits[0];
      setOrgUnitId(selectedOrgUnitId);
    } else {
      console.log("No department/section assigned to the program:", userOrgUnits, programOrgUnits);
      setError("None of your department/section have access to the required program. Please contact administrator.");
      setLoading(false);
    }
  }
}, [userData, programData, loadCompetencyConfig]); 



// FISCAL YEAR LOADING - MAIN COMPONENT (FIXED VERSION)
useEffect(() => {
  const orgUnitId = userData?.me?.organisationUnits?.[0]?.id;
  
  if (!orgUnitId || !engine || fiscalYearLoaded) {
    return;
  }

  const loadFiscalYear = async () => {
    try {
      console.log('🔍 Loading fiscal year for orgUnit:', orgUnitId);
      
      // Query metadata event directly by org unit
      const response = await engine.query({
        events: {
          resource: 'events',
          params: {
            program: 'NkReudUAW4F',
            programStage: 'htIWgNQWEMm',
            ou: orgUnitId,
            ouMode: 'ACCESSIBLE',
            pageSize: 1,
            order: 'eventDate:desc',
            fields: 'event,dataValues[dataElement,value]'
          }
        }
      });
      
      const event = response.events?.events?.[0];
      let startPattern = '', endPattern = '';
      
      if (event) {
        event.dataValues?.forEach(dv => {
          if (dv.dataElement === 'U55MqwBcRAe') startPattern = dv.value;
          if (dv.dataElement === 'kgdk4NXu4f5') endPattern = dv.value;
        });
      }
      
      if (startPattern && endPattern) {
        // Parse the pattern dates (stored as "2000-07-01" format)
        const startDate = new Date(startPattern);
        const endDate = new Date(endPattern);
        
        // Get current date to determine the correct fiscal year
        const now = new Date();
        const currentYear = now.getFullYear();
        
        // For July 1 start, fiscal year runs from July 1 to June 30
        let fiscalStartDate, fiscalEndDate;
        
        // Create July 1 of current year
        const julyFirstCurrentYear = new Date(currentYear, 6, 1); // Month 6 = July
        
        if (now >= julyFirstCurrentYear) {
          // We are in or after July of current year
          fiscalStartDate = new Date(currentYear, startDate.getMonth(), startDate.getDate());
          fiscalEndDate = new Date(currentYear + 1, endDate.getMonth(), endDate.getDate());
        } else {
          // We are before July of current year
          fiscalStartDate = new Date(currentYear - 1, startDate.getMonth(), startDate.getDate());
          fiscalEndDate = new Date(currentYear, endDate.getMonth(), endDate.getDate());
        }
        
        // Format manually to avoid timezone issues
        const formattedStart = `${fiscalStartDate.getFullYear()}-${String(fiscalStartDate.getMonth() + 1).padStart(2, '0')}-${String(fiscalStartDate.getDate()).padStart(2, '0')}`;
        const formattedEnd = `${fiscalEndDate.getFullYear()}-${String(fiscalEndDate.getMonth() + 1).padStart(2, '0')}-${String(fiscalEndDate.getDate()).padStart(2, '0')}`;
        
        console.log('✅ Fiscal year resolved:', {
          storedPattern: { start: startPattern, end: endPattern },
          resolved: { start: formattedStart, end: formattedEnd },
          fiscalYear: `${fiscalStartDate.getFullYear()} - ${fiscalEndDate.getFullYear()}`
        });
        
        setFiscalYear({ start: formattedStart, end: formattedEnd });
      } else {
        console.log('ℹ️ No fiscal year dates found in metadata event');
        const now = new Date();
        const currentYear = now.getFullYear();
        const defaultStart = now >= new Date(currentYear, 6, 1) 
          ? `${currentYear}-07-01` 
          : `${currentYear - 1}-07-01`;
        const defaultEnd = now >= new Date(currentYear, 6, 1)
          ? `${currentYear + 1}-06-30`
          : `${currentYear}-06-30`;
        setFiscalYear({ 
          start: defaultStart, 
          end: defaultEnd 
        });
      }
      setFiscalYearLoaded(true);
      
    } catch (error) {
      console.error('❌ Failed to load fiscal year:', error);
      const now = new Date();
      const currentYear = now.getFullYear();
      setFiscalYear({ 
        start: `${currentYear - 1}-07-01`, 
        end: `${currentYear}-06-30` 
      });
      setFiscalYearLoaded(true);
    }
  };
  
  loadFiscalYear();
}, [userData?.me?.organisationUnits?.[0]?.id, engine, fiscalYearLoaded]);

// Initialize period flags (default to false if dates unavailable)
let isMidYearActive = false;
let isEndYearActive = false;
let isReadOnly = false;

// ✅ ONLY CALCULATE PERIODS IF DATES ARE VALID AND LOADED
if (showHistoricalView) {
  // Historical view - read only
  isReadOnly = true;
} else if (fiscalYear.start && fiscalYear.end) {
  const fiscalStartDate = new Date(fiscalYear.start);
  const fiscalEndDate = new Date(fiscalYear.end);
  
  // Mid-Year: July 1 to December 31
  const midYearStart = new Date(fiscalStartDate);
  const midYearEnd = new Date(fiscalStartDate.getFullYear(), 11, 31); // December 31
  
  // End-Year: January 1 to June 30
  const endYearStart = new Date(fiscalStartDate.getFullYear() + 1, 0, 1); // January 1 of next year
  const endYearEnd = new Date(fiscalEndDate);
  
  const today = new Date();
  isMidYearActive = today >= midYearStart && today <= midYearEnd;
  isEndYearActive = today >= endYearStart && today <= endYearEnd;
  
  if (!isMidYearActive && !isEndYearActive) {
    isReadOnly = true;
  }
  
  console.log('📅 Fiscal Periods Calculated:', {
    fiscalStart: fiscalYear.start,
    fiscalEnd: fiscalYear.end,
    midYear: `${midYearStart.toISOString().split('T')[0]} → ${midYearEnd.toISOString().split('T')[0]}`,
    endYear: `${endYearStart.toISOString().split('T')[0]} → ${endYearEnd.toISOString().split('T')[0]}`,
    today: today.toISOString().split('T')[0],
    isMidYearActive,
    isEndYearActive
  });
} else if (fiscalYearLoaded) {
  console.log('ℹ️ Fiscal year dates not configured - sections hidden. Set dates in Organisation Update.');
}

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  
  const loadAssessments = useCallback(async () => {

    if (isLoadingAssessments) {
      console.log('⏳ Assessment load already in progress, skipping...');
      return;
    }
    
    // For top-level users (like CEO), they should see their direct reports even without a position
    if (!staffList.length) {
      console.log("No staff list available");
      return;
    }

    setIsLoadingAssessments(true);

    console.log("🔄 Loading assessments...");

    try {
      let positionsToLoad = [];

      // If current user has a position, get their direct reports
      if (currentUserPosition) {
        console.log("📋 Loading direct reports for current user position");
        positionsToLoad = await getDirectReports(currentUserPosition);
        console.log(`📋 Found ${positionsToLoad.length} direct report positions from current user position`);
      }
      // If current user is top-level/admin but has no position, try to get all positions that report to them
      else if (isAdmin) {
        console.log("Current user is admin but has no position - loading all positions with incumbents");

        const allPositions = await engine.query({
          positions: {
            resource: 'trackedEntityInstances',
            params: {
              trackedEntityType: 'UGGo0Ebjl0E',
              ouMode: 'ACCESSIBLE',
              fields: 'trackedEntityInstance,attributes[attribute,value]',
              pageSize: 200,
            },
          },
        });

        const allPositionsList = allPositions.positions?.trackedEntityInstances || [];
        console.log(`Found ${allPositionsList.length} total positions`);

        positionsToLoad = allPositionsList.filter(pos => {
          const attrs = extractAttributes(pos.attributes);
          const hasIncumbent = attrs['V66IFsw5kq6'];
          return hasIncumbent;
        });

        console.log(`Found ${positionsToLoad.length} positions with incumbents`);
      } else {
        console.log("❌ Current user has no position and is not admin - cannot load assessments");
      }

      if (positionsToLoad.length === 0) {
        console.log("No positions to load assessments for");
        setDirectReportAssessments([]);
        return;
      }

      const assessments = [];

      for (const position of positionsToLoad) {
        const positionAttrs = extractAttributes(position.attributes);
        const incumbentTei = positionAttrs['V66IFsw5kq6'];

        if (!incumbentTei) {
          console.warn("⚠️ No incumbent TEI found for position:", position.trackedEntityInstance);
          continue;
        }

        const staff = staffList.find(s => s.id === incumbentTei);

        if (!staff) {
          console.warn("⚠️ Staff not found for TEI:", incumbentTei);
          continue;
        }

        if (incumbentTei === teiId) {
          console.log("Skipping current user's own assessment");
          continue;
        }

        console.log(`📥 Loading assessment for staff ${staff.firstName} ${staff.surname} (TEI: ${staff.teiId})`);

        const subordinateCompetencies = await loadCompetencyConfig(staff.orgUnit);
        const assessment = await loadStaffAssessment(staff.teiId, staff.orgUnit, subordinateCompetencies);

        if (assessment && assessment.hasData) {
          assessments.push({
            ...assessment,
            staffName: `${staff.firstName} ${staff.surname}`,
            staffTei: incumbentTei,
            positionTitle: positionAttrs['NWe61Sa41CS'] || 'No Title'
          });
          console.log(`✅ Loaded assessment for: ${staff.firstName} ${staff.surname}`);
        } else {
          console.log(`ℹ️ No assessment data found for ${staff.firstName} ${staff.surname}`);
        }
      }

      console.log(`✅ Total assessments loaded: ${assessments.length}`);
      setDirectReportAssessments(assessments);
      setAssessmentsLoaded(true);

      } catch (error) {
      console.error("❌ Error loading assessments:", error);
      setAssessmentsLoaded(false);
    } finally {
      setIsLoadingAssessments(false);
    }
  }, [staffList, currentUserPosition, isAdmin, teiId, engine, getDirectReports, loadCompetencyConfig, loadStaffAssessment, isLoadingAssessments]);

   
  useEffect(() => {
   
    if (!currentUserPosition || !userData?.me) return;
    const attributes = {};
    currentUserPosition.attributes?.forEach(attr => {
      attributes[attr.attribute] = attr.value;
    });

    const hasManager = !!attributes['SjP47qi3JeB'];
    const isTopLevel = !hasManager;

    console.log(`✅ Position-based hierarchy check: User ${userData.me.username} is ${isTopLevel ? 'TOP LEVEL' : 'has manager'}`);
    
    setHasManager(hasManager);

    if (isTopLevel && !isAdmin) {
      setDirectReportPositions([]);
    }
  }, [userData, currentUserPosition, isAdmin, setHasManager, setDirectReportPositions]);

  useEffect(() => {
    if (staffList.length > 0 && programData?.program && reportingStructureLoaded) {
      console.log("🔄 Conditions met for loading assessments");
      console.log("Staff list count:", staffList.length);
      console.log("Current user position:", currentUserPosition ? "Has position" : "No position");
      console.log("Is admin:", isAdmin);
      
      // Only load if we're in manager_review view OR not in staff view
      // This prevents unnecessary loading
      if (viewMode === 'manager_review') {
        loadAssessments();
      }
    } else {
      console.log("❌ Conditions not met for loading assessments", {
        staffListLength: staffList.length,
        programData: !!programData?.program,
        reportingStructureLoaded
      });
    }
  }, [staffList, programData, reportingStructureLoaded, viewMode]); // Add viewMode as dependency


  const loadReportingStructure = useCallback(async () => {
    if (!staffList.length || !userData?.me?.username) {
      console.log("⚠️ Missing prerequisites for reporting structure");
      return;
    }

    try {
      const orgUnitIds = userData.me.organisationUnits.map(ou => ou.id).join(';');

      const { positions } = await engine.query({
        positions: {
          resource: 'trackedEntityInstances',
          params: {
            trackedEntityType: 'UGGo0Ebjl0E',
            ou: orgUnitIds,
            ouMode: 'ACCESSIBLE',
            fields: 'trackedEntityInstance,attributes[attribute,value]',
            pageSize: 100,
          },
        },
      });

      // Find current user by username
      const currentUser = staffList.find(s => s.username === userData.me.username);
      if (!currentUser) {
        console.log("❌ Current user not found in staff list");
        return;
      }

      
      const userId = currentUser.id; 
      const userPosition = positions.trackedEntityInstances.find(pos => {
        const incumbent = pos.attributes?.find(a => a.attribute === "V66IFsw5kq6");
        return incumbent?.value === userId; 
      });

      if (!userPosition) {
        console.log("❌ No position found for user TEI:", currentUser.teiId);
        setCurrentUserPosition(null);
        setHasManager(false); 
        return;
      }

      console.log("✅ Found position for user", currentUser.username, ":", userPosition.trackedEntityInstance);
      setCurrentUserPosition(userPosition);

      // Check if user has a manager by looking at "Reports to Position" attribute
      const positionAttrs = extractAttributes(userPosition.attributes);
      const reportingToPositionTei = positionAttrs['SjP47qi3JeB']; // Reports to Position attribute

      console.log("Position attributes:", {
        positionId: userPosition.trackedEntityInstance,
        reportingTo: reportingToPositionTei || 'None'
      });

      // Set hasManager based on whether reportingTo exists and is not empty
      const hasMgr = !!(reportingToPositionTei && reportingToPositionTei.trim() !== '');
      setHasManager(hasMgr);
      console.log(`User ${hasMgr ? 'HAS' : 'DOES NOT HAVE'} a manager`);

      // Load direct reports (only if user has subordinates)
      if (hasMgr || isAdmin) {
        const directReports = await getDirectReports(userPosition);
        console.log("📋 Found", directReports.length, "direct report positions");
        setDirectReportPositions(directReports);
      } else {
        console.log("User has no subordinates to manage");
        setDirectReportPositions([]);
      }

      setReportingStructureLoaded(true);

    } catch (error) {
      console.error("Error loading reporting structure:", error);
      setReportingStructureLoaded(false);
      setHasManager(false); // Default to no manager on error
    }
  }, [staffList, userData, engine, isAdmin]);

  useEffect(() => {
    if (teiId && staffList.length > 0 && !reportingStructureLoaded) {
      console.log("🔄 Loading reporting structure...");
      console.log("TEI:", teiId);
      console.log("Staff list count:", staffList.length);

      // Find current user in staff list
      const currentUserStaff = staffList.find(s => s.teiId === teiId);
      console.log("Current user in staff list:", currentUserStaff);

      loadReportingStructure();
    }
  }, [teiId, staffList, reportingStructureLoaded, loadReportingStructure]);


  useEffect(() => {
    if (teiId && staffList.length > 0 && !reportingStructureLoaded) {
      loadReportingStructure();
    }
  }, [teiId, staffList, reportingStructureLoaded, loadReportingStructure]);


  const loadStaffList = useCallback(async (engine, userData, programData) => {
    if (!userData?.me?.organisationUnits?.length || !programData?.program) return;

    try {
      const orgUnitIds = userData.me.organisationUnits.map(ou => ou.id).join(';');

      // 1. Load ALL Person TEIs
      const { teis } = await engine.query({
        teis: {
          resource: 'trackedEntityInstances',
          params: {
            trackedEntityType: CONFIG.trackedEntityType,
            ou: orgUnitIds,
            ouMode: 'DESCENDANTS',
            fields: 'trackedEntityInstance,orgUnit,attributes[attribute,value]',
            pageSize: 500,
          },
        },
      });

      // 2. Load ALL system users
      const { users } = await engine.query({
        users: {
          resource: 'users',
          params: {
            fields: 'id,username,name,firstName,surname,userCredentials[username],email,organisationUnits[id,level,name,path]',
            ou: orgUnitIds,
            ouMode: 'ACCESSIBLE',
            pageSize: 1000,
          },
        },
      });

      // 3. Create username → user map
      const usernameToUserMap = {};
      users?.users?.forEach(user => {
        const username = user.userCredentials?.username;
        if (username) {
          usernameToUserMap[username] = user;
        }
      });

      // 4. Build staff list FROM PERSON TEIs
      const staffList = teis.trackedEntityInstances.map(tei => {
        const usernameAttr = tei.attributes?.find(attr => attr.attribute === "g3RRIfDiQra");
        const username = usernameAttr?.value || null;
        const user = username ? usernameToUserMap[username] : null;

        return {
          id: user?.id || tei.trackedEntityInstance, 
          teiId: tei.trackedEntityInstance,        
          orgUnit: tei.orgUnit,
          username: username,
          firstName: user?.firstName || 'Unknown',
          surname: user?.surname || 'Staff',
          userCredentials: user?.userCredentials || { username },
          positionTei: null,
          positionTitle: null,
        };
      });

      // 5. Load positions and link them
      const { positions } = await engine.query({
        positions: {
          resource: 'trackedEntityInstances',
          params: {
            trackedEntityType: 'UGGo0Ebjl0E',
            ou: orgUnitIds,
            ouMode: 'ACCESSIBLE',
            fields: 'trackedEntityInstance,attributes[attribute,value]',
            pageSize: 100,
          },
        },
      });

      // 🔥 MAP: user ID → Position TEI (V66IFsw5kq6 = user ID)
      const userIdToPositionMap = {};
      positions.trackedEntityInstances?.forEach(pos => {
        const incumbentUserId = pos.attributes?.find(a => a.attribute === "V66IFsw5kq6")?.value;
        if (incumbentUserId) {
          userIdToPositionMap[incumbentUserId] = pos.trackedEntityInstance;
        }
      });

      // Map: Position TEI → Position Title
      const positionTitleMap = {};
      positions.trackedEntityInstances?.forEach(pos => {
        const title = pos.attributes?.find(a => a.attribute === "NWe61Sa41CS")?.value;
        positionTitleMap[pos.trackedEntityInstance] = title || 'No Title';
      });

      // ATTACH POSITION INFO USING USER ID
      const staffWithPositions = staffList.map(staff => ({
        ...staff,
        positionTei: userIdToPositionMap[staff.id] || null,
        positionTitle: userIdToPositionMap[staff.id]
          ? positionTitleMap[userIdToPositionMap[staff.id]]
          : null,
      }));

      console.log("✅ Final Staff List:", staffWithPositions);
      setStaffList(staffWithPositions);
    } catch (error) {
      console.error("❌ Error loading staff list:", error);
      setStaffList([]);
    }
  }, [engine, CONFIG.trackedEntityType]);

  const checkCompetencyUsage = async (competencyName) => {
    try {
      console.log(`🔍 Checking if competency "${competencyName}" is in use...`);

      // Quick check - just see if ANY event exists with this competency
      const response = await engine.query({
        events: {
          resource: 'events',
          params: {
            program: CONFIG.program,
            fields: 'programStage',
            filter: `${CONFIG.de.competencyCode}:eq:${competencyName}`,
            pageSize: 1, 
            totalPages: true
          }
        }
      });

      const totalCount = response.events?.pager?.total || 0;

      return {
        isUsed: totalCount > 0,
        totalCount,
        message: totalCount > 0
          ? `This competency is used by ${totalCount} staff member(s)`
          : 'Not in use'
      };

    } catch (error) {
      console.error("Error checking competency usage:", error);
      return {
        isUsed: true, // Default to "used" for safety
        totalCount: 999,
        message: "Unable to verify - deletion blocked for safety"
      };
    }
  };


  // useEffect to trigger staff list loading
  useEffect(() => {
    if (userData?.me && programData?.program) {
      console.log("📋 Triggering loadStaffList...");
      loadStaffList(engine, userData, programData);
    }
  }, [userData, programData, engine, loadStaffList]);

  // Call loadStaffList when user data is ready
  useEffect(() => {
    if (userData?.me) {
      console.log("📋 User data ready, loading staff list...");
      loadStaffList();
    }
  }, [userData?.me]);

  // Helper function to ensure enrollment exists
  const ensureEnrollment = useCallback(async (teiId, currentOrgUnitId) => {
    if (!currentOrgUnitId) {
      throw new Error("Organisation unit is required for enrollment");
    }

    try {
      const teiQuery = {
        tei: {
          resource: `trackedEntityInstances/${teiId}`,
          params: {
            fields: "enrollments[enrollment,program,orgUnit,status]",
          },
      },
    };

    try {
      const { tei } = await engine.query(teiQuery);
      console.log("TEI query result:", tei);

      // Look for ANY active enrollment in the program (regardless of orgUnit)
      const programEnrollment = tei?.enrollments?.find(
        e => e.program === CONFIG.program && e.status === "ACTIVE"
      );

      if (programEnrollment) {
        console.log("✅ Found existing ACTIVE enrollment:", programEnrollment.enrollment);
        return programEnrollment.enrollment;
      } else {
        console.log("ℹ️ No active enrollment found for program", CONFIG.program, "for TEI", teiId);
      }
    } catch (teiError) {
      console.log("Could not get TEI with enrollments, trying alternative approach...", teiError);
    }

    // Only create enrollment if no active one exists
    const enrollmentPayload = {
      trackedEntityInstance: teiId,
      program: CONFIG.program,
      orgUnit: currentOrgUnitId,
      enrollmentDate: new Date().toISOString().split('T')[0],
      incidentDate: new Date().toISOString().split('T')[0],
      status: "ACTIVE",
    };

    console.log("Enrollment creation payload:", enrollmentPayload);

    const enrollmentRes = await engine.mutate({
      resource: "enrollments",
      type: "create",
      data: enrollmentPayload,
    });

    console.log("Enrollment creation response:", enrollmentRes);

    let enrollmentId = enrollmentRes?.response?.uid || enrollmentRes?.response?.enrollment;

    if (!enrollmentId && enrollmentRes.response?.importSummaries?.[0]?.reference) {
      enrollmentId = enrollmentRes.response.importSummaries[0].reference;
    }

    if (!enrollmentId) {
      if (enrollmentRes.response?.response?.uid) {
        enrollmentId = enrollmentRes.response.response.uid;
      } else if (enrollmentRes.response?.response?.enrollment) {
        enrollmentId = enrollmentRes.response.response.enrollment;
      }
    }

    if (!enrollmentId) {
      console.error("Failed to extract enrollment ID from:", enrollmentRes);
      throw new Error("Failed to create enrollment - no enrollment ID returned");
    }

    console.log("Enrollment created:", enrollmentId);
    return enrollmentId;

  } catch (error) {
    console.error("❌ Error ensuring enrollment:", error);
    
    //  Check if error is about duplicate enrollment
    if (error.message?.includes("already has an active enrollment")) {
      // Try to fetch the existing enrollment
      console.log("Duplicate enrollment detected, trying to fetch existing...");
      const { tei } = await engine.query({
        tei: {
          resource: `trackedEntityInstances/${teiId}`,
          params: {
            fields: "enrollments[enrollment,program,status]",
          },
        },
      });
      
      const existingEnrollment = tei?.enrollments?.find(e => e.program === CONFIG.program && e.status === "ACTIVE");
      if (existingEnrollment) {
        console.log("✅ Retrieved existing enrollment:", existingEnrollment.enrollment);
        return existingEnrollment.enrollment;
      }
    }
    
    throw error;
  }
}, [engine]);

  // ===== Load review after TEI is ready =====

  const loadReview = useCallback(async (teiIdParam, currentOrgUnitId) => {
    const currentTeiId = teiIdParam || teiId;
    const currentOuId = currentOrgUnitId || orgUnitId;
    if (!currentOuId || !currentTeiId) return;

    try {
      const query = {
        events: {
          resource: "events",
          params: {
            program: CONFIG.program,
            trackedEntityInstance: currentTeiId,
            orgUnit: currentOuId,
            fields: "event,programStage,dataValues[dataElement,value]",
            pageSize: 200,
          },
        },
      };
      const result = await engine.query(query);
      const events = result.events?.events || [];

      // PROCESS ONLY RELEVANT EVENT TYPES
      const weightEvents = events.filter(e => e.programStage === CONFIG.stages.config);
      const midYearEvents = events.filter(e => e.programStage === CONFIG.stages.midYear);
      const endYearEvents = events.filter(e => e.programStage === CONFIG.stages.endYear);
      // REMOVED: approvalEvents

      setCompetencies(prevComps => {
        const updatedComps = prevComps.map(comp => {
          const weightEvent = weightEvents.find(e => {
            const idDV = e.dataValues.find(dv => dv.dataElement === CONFIG.de.competencyId);
            return idDV?.value === comp.competencyId;
          });

          const midEvent = midYearEvents.find(e => {
            const idDV = e.dataValues.find(dv => dv.dataElement === CONFIG.de.competencyId);
            return idDV?.value === comp.competencyId;
          });

          const endEvent = endYearEvents.find(e => {
            const idDV = e.dataValues.find(dv => dv.dataElement === CONFIG.de.competencyId);
            return idDV?.value === comp.competencyId;
          });

          // REMOVED: approvalEvent handling

          return {
            ...comp,
            weight: weightEvent
              ? parseFloat(weightEvent.dataValues.find(dv => dv.dataElement === CONFIG.de.weightValue)?.value)
              : comp.weight,
            weightLocked: !!weightEvent,

            ratings: [
              midEvent ? parseInt(midEvent.dataValues.find(dv => dv.dataElement === CONFIG.de.ratingScore)?.value) : null,
              endEvent ? parseInt(endEvent.dataValues.find(dv => dv.dataElement === CONFIG.de.ratingScore)?.value) : null
            ],
            comments: [
              midEvent ? midEvent.dataValues.find(dv => dv.dataElement === CONFIG.de.ratingComment)?.value || "" : "",
              endEvent ? endEvent.dataValues.find(dv => dv.dataElement === CONFIG.de.ratingComment)?.value || "" : ""
            ]
          };
        });
        return updatedComps;
      });
    } catch (error) {
      console.error("Error loading review:", error);
    }
  }, [engine, orgUnitId, teiId]);


  const getExistingTei = useCallback(async (currentOrgUnitId) => {
    if (!currentOrgUnitId) throw new Error("OrgUnit not defined");
    if (!userData?.me?.username) throw new Error("User data not ready");

    console.log("🔍 Searching for existing TEI for user:", userData.me.username, "in orgUnit:", currentOrgUnitId);

    // 1. Try staff list first (cached)
    const existingStaff = staffList.find(s => s.username === userData.me.username);
    if (existingStaff?.teiId) {
      console.log("✅ Found TEI from staff list:", existingStaff.teiId);
      return existingStaff.teiId;
    }

    // 2. Query API directly — but only within current org unit subtree
    try {
      const query = {
        teis: {
          resource: "trackedEntityInstances",
          params: {
            trackedEntityType: CONFIG.trackedEntityType, // 'OaAbpIs1cV7'
            ou: currentOrgUnitId,
            ouMode: 'DESCENDANTS',
            fields: "trackedEntityInstance",
            filter: `g3RRIfDiQra:eq:${userData.me.username}`,
            pageSize: 1,
          },
        },
      };

      const { teis } = await engine.query(query);
      const results = teis?.trackedEntityInstances || [];

      if (results.length === 0) {
        // 🔴 Do NOT create — throw meaningful error
        throw new Error(`No staff record found for username '${userData.me.username}' in org unit ${currentOrgUnitId}. Please ensure your user is linked to a Person TEI.`);
      }

      const teiId = results[0].trackedEntityInstance;
      console.log("✅ Found TEI via API:", teiId);
      return teiId;

    } catch (error) {
      console.error("❌ Failed to find existing TEI:", error);
      throw error;
    }
  }, [engine, userData, staffList, CONFIG.trackedEntityType]);

  useEffect(() => {
    const initTei = async () => {
        if (!orgUnitId || !userData?.me?.username) {
          return;
        }

        try {
          setLoading(true);
          setError(null);

          // Only get existing TEI — no creation
          const teiId = await getExistingTei(orgUnitId);
          
          // Now ensure enrollment
          try {
            const enrollmentId = await ensureEnrollment(teiId, orgUnitId);
            setEnrollmentId(enrollmentId);
          } catch (enrollmentError) {
            console.warn("Enrollment error (might already exist):", enrollmentError);
            
            // Even if enrollment fails, try to load review
            // The enrollment might already exist from a previous session
          }

          setTeiId(teiId);
          await loadReview(teiId, orgUnitId);
          
        } catch (err) {
          console.error("TEI initialization failed:", err);
          setError(err.message);
        } finally {
          setLoading(false);
        }
      };

    initTei();
  }, [orgUnitId, userData, getExistingTei, ensureEnrollment, loadReview]);



  useEffect(() => {
    if (teiId && orgUnitId) {
      loadReview(teiId, orgUnitId);
    }
  }, [teiId, orgUnitId, loadReview]);

  // Reload staff's data when switching to staff view or when dependencies change
  useEffect(() => {
    if ((viewMode === 'staff' || (!isAdmin && !hasManager)) && teiId && orgUnitId && templateCompetencies.length > 0 && fiscalYear.start && fiscalYear.end) {
      console.log('🔄 Reloading staff data for Staff view...');
      loadCurrentYearData();
    }
  }, [viewMode, teiId, orgUnitId, templateCompetencies, fiscalYear.start, fiscalYear.end]);


  const saveCompetencyEvent = async (comp, programStage, dataValues) => {
    if (!teiId || !orgUnitId || !enrollmentId) {
      console.error("TEI, OrgUnit, or Enrollment not available");
      return;
    }

    try {
      //  FIRST: Get ALL events for this competency and stage
      const response = await engine.query({
        events: {
          resource: 'events',
          params: {
            program: CONFIG.program,
            programStage: programStage,
            trackedEntityInstance: teiId,
            fields: 'event,eventDate,created,status,dataValues[dataElement,value]',
            filter: `${CONFIG.de.competencyId}:eq:${comp.competencyId}`,
            order: 'created:desc',
            pageSize: 10
          }
        }
      });

      const events = response.events?.events || [];
      console.log(`📊 Found ${events.length} existing events for ${comp.name} (${programStage})`);

      if (events.length > 0) {
        // ✅ ALWAYS UPDATE - never create new
        const targetEvent = events[0];
        console.log(`🔁 Updating existing event ${targetEvent.event} for ${comp.name}`);

        // Get current date for eventDate
        const eventDate = new Date().toISOString().split('T')[0];

        // Build the update payload - DON'T fetch the full event first
        const updateData = {
          event: targetEvent.event,
          program: CONFIG.program,
          programStage: programStage,
          orgUnit: orgUnitId,
          trackedEntityInstance: teiId,
          enrollment: enrollmentId,
          eventDate: eventDate,
          status: "COMPLETED",
          dataValues: dataValues
        };

        console.log("Update payload:", updateData);

        // Direct update without fetching first
        await engine.mutate({
          type: 'update',
          resource: `events/${targetEvent.event}`,
          data: updateData,
          headers: { 'Content-Type': 'application/json' }
        });

        console.log(`✅ Updated event ${targetEvent.event} for ${comp.name}`);
        return targetEvent.event;

      } else {
        // This shouldn't happen after cleanup, but just in case
        console.log(`🆕 No existing event found, creating new for ${comp.name}`);

        const eventData = {
          program: CONFIG.program,
          programStage: programStage,
          trackedEntityInstance: teiId,
          orgUnit: orgUnitId,
          enrollment: enrollmentId,
          eventDate: new Date().toISOString().split('T')[0],
          status: "COMPLETED",
          dataValues: dataValues,
        };

        const result = await engine.mutate({
          resource: "events",
          type: "create",
          data: eventData,
        });

        console.log(`✅ Created new event for ${comp.name}`);
        return result.response?.reference;
      }

    } catch (error) {
      console.error(`❌ Error saving event for ${comp.name}:`, error);

      // More detailed error info
      if (error.response?.importSummaries?.[0]) {
        const summary = error.response.importSummaries[0];
        console.error("Import summary:", {
          status: summary.status,
          description: summary.description,
          conflicts: summary.conflicts
        });
      }

      throw error;
    }
  };

  const handleDirectReportApproval = async (assessment, compIndex, status) => {
    const comp = assessment.competencies[compIndex];
    const remark = window.prompt(`Enter remarks for ${status}:`, comp.approvalRemark || '');
    if (remark === null) return;

    try {
      // Ensure subordinate has an enrollment
      const subordinateEnrollmentId = await ensureEnrollment(assessment.teiId, assessment.orgUnit || orgUnitId);

      // ✅ CORRECT: Save approval in approvals stage
      const eventData = {
        program: CONFIG.program,
        programStage: CONFIG.stages.approvals,
        trackedEntityInstance: assessment.teiId,
        orgUnit: assessment.orgUnit || orgUnitId,
        enrollment: subordinateEnrollmentId,
        eventDate: new Date().toISOString().split('T')[0],
        status: "COMPLETED",
        dataValues: [
          { dataElement: CONFIG.de.competencyId, value: comp.competencyId },
          { dataElement: CONFIG.de.approvalStatus, value: status },
          { dataElement: CONFIG.de.approvalRemark, value: remark }
        ]
      };

      console.log("Creating approval event in approvals stage:", eventData);

      const result = await engine.mutate({
        resource: "events",
        type: "create",
        data: eventData,
      });

      console.log("✅ Approval saved:", result);

      // Update local state
      setDirectReportAssessments(prev =>
        prev.map(a =>
          a.teiId === assessment.teiId
            ? {
              ...a,
              competencies: a.competencies.map((c, idx) =>
                idx === compIndex
                  ? { ...c, approvalStatus: status, approvalRemark: remark }
                  : c
              )
            }
            : a
        )
      );

      alert(`✅ Competency ${status} successfully!`);
    } catch (error) {
      console.error("❌ Approval error:", error);
      alert("Failed to save approval.");
    }
  };


  // Helper function to find existing weight event using COMPOSITE KEY (name + definition)
  const findWeightEventForCompetency = async (staffTei, competencyId) => {
    try {
      const query = {
        events: {
          resource: 'events',
          params: {
            program: CONFIG.program,
            programStage: CONFIG.stages.weights,
            trackedEntityInstance: staffTei,
            fields: 'event,dataValues[dataElement,value]',
            filter: `${CONFIG.de.competencyId}:eq:${competencyId}`,
            pageSize: 1
          }
        }
      };
      const result = await engine.query(query);
      return result.events?.events?.[0] || null;
    } catch (error) {
      console.error("Error finding weight event:", error);
      return null;
    }
  };

  // ===== Handlers =====
  const handleWeightChange = useCallback((index, value) => {
    if (!isAdmin && weightSetupComplete) return;

    const parsed = value === "" || value == null || isNaN(Number(value))
    ? null
    : parseFloat(value);
    setCompetencies((prev) => {
      const newComps = [...prev];
      // Only update the specific item
      newComps[index] = {
        ...newComps[index],
        weight: value // value is already 0-1 decimal from WeightInput
      };
      return newComps;
    });
  }, [weightSetupComplete]);

  // ONLY update local state — no save, no marking as submitted
  const handleRatingChange = useCallback(async (compIndex, ratingIndex, value) => {
    if (!weightSetupComplete) return;

    const comp = competencies[compIndex];
    
    // Just update the rating value locally - DO NOT change submission status
    const newRatings = [...comp.ratings];
    newRatings[ratingIndex] = value ? parseInt(value) : null;

    // Update local state ONLY - NO automatic submission
    setCompetencies(prev =>
      prev.map((c, idx) =>
        idx === compIndex
          ? {
              ...c,
              ratings: newRatings,
              // Keep existing approval status - don't auto-change
              approvalStatus: c.approvalStatus,
              approvalRemark: c.approvalRemark,
            }
          : c
      )
    );
  }, [weightSetupComplete, competencies]);
  
  const handleManualSave = async (compIndex, ratingIndex) => {
  const comp = competencies[compIndex];
  const ratingValue = comp.ratings[ratingIndex];

  // Validation
  if (ratingValue === null || ratingValue === undefined) {
    alert("Please select a rating before saving.");
    return;
  }

  if (!comp.comments[ratingIndex]?.trim()) {
    alert("Please add a comment before saving.");
    return;
  }

  // Now save to database
  try {
    const programStage = ratingIndex === 0 ? CONFIG.stages.midYear : CONFIG.stages.endYear;
    const dataValues = [
      { dataElement: CONFIG.de.competencyId, value: comp.competencyId },
      { dataElement: CONFIG.de.ratingScore, value: ratingValue.toString() },
      { dataElement: CONFIG.de.ratingComment, value: comp.comments[ratingIndex] },
    ];

    await saveCompetencyEvent(comp, programStage, dataValues);

      setNotification({
        visible: true,
        type: 'success',
        title: 'Rating saved successfully',
        details: `${comp.name} - ${ratingIndex === 0 ? 'Mid-Year' : 'End-Year'}`
      });
    } catch (error) {
      console.error("Failed to save rating:", error);
      setNotification({
        visible: true,
        type: 'critical',
        title: 'Failed to save rating',
        details: error.message || 'Please try again.'
      });
    }
  };

  // Add a saveRatingForCompetency function:
  const saveRatingForCompetency = async (comp, ratingIndex, ratingValue, comment = "") => {
    const programStage = ratingIndex === 0 ? CONFIG.stages.midYear : CONFIG.stages.endYear;

    // ✅ INCLUDE competencyDefinition
    const dataValues = [
      { dataElement: CONFIG.de.competencyId, value: comp.competencyId },
      { dataElement: CONFIG.de.ratingScore, value: ratingValue.toString() },
      { dataElement: CONFIG.de.ratingComment, value: comment }
    ];

    return saveCompetencyEvent(comp, programStage, dataValues);
  };

  const handleConfirmRating = async (ratingIndex) => {
    setSavingRatings({ [ratingIndex]: true });
    try {

      const incomplete = competencies.find(comp => {
      const hasRating = comp.ratings[ratingIndex] !== null;
      const hasComment = comp.comments[ratingIndex]?.trim();
      return hasRating && !hasComment;
        });

        if (incomplete) {
          alert(`Please add a comment for "${incomplete.name}" before confirming.`);
          return;
        }

      // Save all ratings for the given period (mid-year or end-year)
      for (const comp of competencies) {
        const ratingValue = comp.ratings[ratingIndex];
        if (ratingValue !== null && ratingValue !== undefined) {
          await saveRatingForCompetency(comp, ratingIndex, ratingValue);

          }
      }

      alert(`${ratingIndex === 0 ? 'Mid-Year' : 'End-Year'} ratings confirmed successfully!`);
    } catch (error) {
      console.error('❌ Error saving ratings:', error);
      alert(`Failed to save ratings: ${error.message}`);
    } finally {
      setSavingRatings({ [ratingIndex]: false });
    }
  };

  const totalWeight = React.useMemo(() => {
      return competencies.reduce((sum, c) => sum + (c.weight || 0), 0);
    }, [competencies]);
  const isConfirmDisabled = Math.abs(totalWeight - 1) > 0.001;


  // ===== Loading Components =====
  const LoadingSpinner = ({ small = false }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: small ? '0' : '20px' }}>
      <CircularLoader small={small} />
      {!small && <span style={{ marginLeft: '10px' }}>Please Wait Loading...</span>}
    </div>
  );

  const SavingButton = ({ children, saving, ...props }) => (
    <Button {...props} disabled={props.disabled || saving}>
      {saving ? <CircularLoader small /> : children}
    </Button>
  );


  // ===== Render =====
  if (userLoading || programLoading || loading) return <LoadingSpinner small />;
  if (userError) return <div>Error loading user data: {userError.message}</div>;
  if (programError) return <div>Error loading program data: {programError.message}</div>;
  if (error) return (
    <div style={{ padding: '20px', color: 'red' }}>
      <h3>Error: {error}</h3>
      <p>Please check if you have a company department assigned.</p>
      <Button onClick={() => window.location.reload()}>Retry</Button>
    </div>
  );


  if (competencies.length === 0) {
    if (isAdmin) {
      return (
        <div style={{ padding: '24px', textAlign: 'center' }}>
          <h3>No Leadership Competencies Configured</h3>
          <p>As an admin, you must define competencies before staff can use this module.</p>
          <Button
            primary
            onClick={() => setCompetencies([{
              name: '',
              definition: '',
              eventId: null,
              weight: null,
              weightLocked: false,
              ratings: [null, null],
              ratingsLocked: [false, false],
              comments: ["", ""],
            }])}
          >
            + Create First Competency
          </Button>
        </div>
      );
    } else {
      return (
        <div style={{ padding: '24px', textAlign: 'center' }}>
          <h3>Leadership Assessment Not Available</h3>
          <p>Your department administrator has not yet configured leadership competencies.</p>
          <p>Please contact your administrator to set up this module.</p>
        </div>
      );
    }
  }

  const DirectReportAssessmentView = ({ assessment, staff }) => {
    const staffName = staff ? `${staff.firstName} ${staff.surname}` : 'Unknown Staff';

    const calculateScore = (comp) => {
      // Include all non-null ratings, regardless of locked status
      const filled = comp.ratings.filter(r => r != null);
      if (!filled.length) return 0;
      const avg = filled.reduce((a, b) => a + b, 0) / filled.length;
      return (comp.weight || 0) * avg;
    };

    const calculateFinalScore = () =>
      (assessment.competencies || []).reduce((sum, c) => sum + calculateScore(c), 0).toFixed(2);

    const hasPending = assessment.competencies.some(c => c.approvalStatus === 'pending');

    const isLevel5Org = React.useMemo(() => {
      if (!userData?.me?.organisationUnits?.[0]?.path) return false;
      const pathParts = userData.me.organisationUnits[0].path.split('/').filter(Boolean);
      // Level 5 orgs have path length of 5 (root + 4 levels)
      return pathParts.length === 5;
    }, [userData]);

   return (
      <div style={{ backgroundColor: 'white', padding: '1rem', borderRadius: '6px', marginBottom: '1rem' }}>
        <h5 style={{ marginBottom: '0.5rem', color: '#2c6693' }}>
          {staffName}'s Leadership Assessment
        </h5>
        {/* Competency List */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem' }}>
          {assessment.competencies.map((comp, idx) => (
            <div key={idx} style={{ padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>{comp.name}</div>
              <div>Weight: {comp.weight ? (comp.weight * 100).toFixed(0) + '%' : 'Not set'}</div>
              <div>Mid-Year: {comp.ratings[0] !== null ? comp.ratings[0] : 'Not rated'}</div>
              <div>End-Year: {comp.ratings[1] !== null ? comp.ratings[1] : 'Not rated'}</div>
              <div>Score: {calculateScore(comp).toFixed(2)}</div>

              {/* Approval Status */}
              <div style={{ marginTop: '8px', fontSize: '0.85rem', borderTop: '1px solid #eee', paddingTop: '4px' }}>
                <strong>Status:</strong> {comp.approvalStatus || 'pending'}
                {comp.approvalRemark && (
                  <div style={{ fontStyle: 'italic', color: '#666', marginTop: '4px' }}>
                    Remark: "{comp.approvalRemark}"
                  </div>
                )}
              </div>

            </div>
          ))}
        </div>
        <div style={{ textAlign: 'center', fontWeight: 'bold', color: '#2c6693', marginTop: '1rem' }}>
          Total Score: {calculateFinalScore()}
        </div>
      </div>
    );
  };

 

  return (
  <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "24px" }}>
    <h3 style={{ marginBottom: "24px", color: "#2c6693" }}>
      Leadership and Behaviour Management
    </h3>

    {/* Fiscal Year Selector */}
    {(isAdmin || hasManager) && (
      <div style={{ 
        marginBottom: '24px', 
        padding: '16px', 
        backgroundColor: '#f0f4f8', 
        borderRadius: '8px',
        border: '1px solid #d0d7de'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <label style={{ fontWeight: 600, marginRight: '12px' }}>Current Fiscal Year:</label>
            <span style={{ 
              padding: '4px 12px', 
              backgroundColor: fiscalYear.start && fiscalYear.end ? '#4caf50' : '#ff9800',
              color: 'white',
              borderRadius: '4px',
              fontWeight: 500
            }}>
              {fiscalYear.start && fiscalYear.end 
                ? `${new Date(fiscalYear.start).getFullYear()} - ${new Date(fiscalYear.end).getFullYear()}`
                : 'Not Configured'}
            </span>
          </div>
          
          {isAdmin && availableFiscalYears.length > 0 && (
            <div>
              <label style={{ fontWeight: 600, marginRight: '12px' }}>View Historical:</label>
              <select
                value={selectedFiscalYear || ''}
                onChange={(e) => {
                  const yearValue = parseInt(e.target.value);
                  if (yearValue) {
                    const yearData = availableFiscalYears.find(y => y.year === yearValue);
                    if (yearData) {
                      if (yearData.isCurrent) {
                        setSelectedFiscalYear(null);
                        setShowHistoricalView(false);
                        window.location.reload();
                      } else {
                        loadHistoricalAssessments(yearData.year);
                      }
                    }
                  } else {
                    setSelectedFiscalYear(null);
                    setShowHistoricalView(false);
                    window.location.reload();
                  }
                }}
                style={{
                  padding: '8px 12px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  backgroundColor: 'white',
                  minWidth: '150px'
                }}
              >
                <option value="">-- Current Year --</option>
                {availableFiscalYears
                  .filter(fy => !fy.isCurrent)
                  .map(fy => (
                    <option key={fy.year} value={fy.year}>
                      {fy.label}
                    </option>
                  ))}
              </select>
            </div>
          )}
        </div>
        
        {showHistoricalView && selectedFiscalYear && (
          <div style={{
            marginTop: '12px',
            padding: '8px 12px',
            backgroundColor: '#fff3e0',
            borderRadius: '4px',
            fontSize: '0.9rem'
          }}>
            <IconView16 /> Viewing historical data for fiscal year {selectedFiscalYear} - {selectedFiscalYear + 1}
            <button
              onClick={() => {
                setSelectedFiscalYear(null);
                setShowHistoricalView(false);
                window.location.reload();
              }}
              style={{
                marginLeft: '12px',
                padding: '4px 8px',
                backgroundColor: '#ff9800',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Back to Current
            </button>
          </div>
        )}
      </div>
    )}

    {/* ADMIN CONFIGURATION UI - Add this after Fiscal Year Selector */}

    {/* Rating Scale Configuration - Admin Only */}
      {!readOnlyReport && isAdmin && (
        <RatingScaleAdmin 
          orgUnitId={topOrgUnit || orgUnitId}
          orgUnitName={userData?.me?.organisationUnits?.[0]?.name}
        />
      )}

  {!readOnlyReport && isAdmin && (
    <div style={{ marginBottom: '24px', padding: '16px', border: '1px solid #ccc', borderRadius: '8px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <h4>Leadership Values Configuration</h4>
        <Button small primary onClick={addNewCompetency}>
          + Add Value
        </Button>
      </div>
    

      {/* Definition Edit Section (Draggable) */}
      {competencies.map((comp, idx) => (
        <div
          key={comp.competencyId || comp.tempId || idx}
        draggable
        onDragStart={(e) => handleDragStart(e, idx)}
        onDragOver={(e) => e.preventDefault()} 
        onDrop={(e) => handleDrop(e, idx)}
        style={{
          display: 'flex',
          gap: '12px',
          alignItems: 'flex-start',
          marginBottom: '12px',
          padding: '12px',
          border: '1px solid #e1e5e9',
          borderRadius: '6px',
          backgroundColor: '#d9f8dfff',
          cursor: 'grab',
          boxShadow: '0 4px 8px rgba(93, 90, 90, 1)',
        }}
      >
        {/* Drag handle */}
        <div
          style={{
            width: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#888',
            cursor: 'grab',
            userSelect: 'none',
          }}
          aria-label="Drag to reorder"
        >
          <IconDragHandle24 />
        </div>

        <div style={{ flex: '0 0 200px', maxWidth: '200px' }}>
          <InputField
            value={comp.name}
            onChange={({ value }) => updateCompetency(idx, 'name', value)}
            placeholder="Value Name"
            label="Name"
            dense
          />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <label style={{ fontWeight: 500, marginBottom: '4px', display: 'block', fontSize: '0.85rem' }}>
            Definition
          </label>
          <RichTextEditor
            value={comp.definition}
            onChange={(value) => updateCompetency(idx, 'definition', value)}
            placeholder="Enter detailed value definition with bullet points, lists, etc..."
            rows={4}
            disabled={false}
          />
        </div>
        <div style={{ flexShrink: 0, paddingTop: '28px' }}>
          <Button
            small
            destructive
            onClick={() => deleteCompetency(idx)}
          >
            Delete
          </Button>
        </div>
      </div>
    ))}

    {/* Weights Section */}
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        marginBottom: "24px",
        padding: "16px",
        backgroundColor: "#f8f9fa",
        borderRadius: "8px",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      }}
    >
      {/* Weight Validation Banner */}
      <div style={{ marginBottom: "16px" }}>
        {weightSetupComplete ? (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "12px 16px",
              backgroundColor: "#e8f5e8",
              border: `2px solid #4caf50`,
              borderRadius: "4px",
            }}
          >
            <div>
              <p style={{ margin: "0 0 4px 0", fontWeight: "500", color: "#2e7d32" }}>
                ✅ Total weight is 100%
              </p>
              <p style={{ margin: 0, fontSize: "0.9em", color: "#555" }}>
                You may now save or proceed with assessments.
              </p>
            </div>
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "12px 16px",
              backgroundColor: "#fff8e1",
              border: `2px solid #ffc107`,
              borderRadius: "4px",
              marginBottom: "16px",
            }}
          >
            <div>
              <p style={{ margin: "0 0 4px 0", fontWeight: "500", color: "#e65100" }}>
                ⚠️ Total weight must equal 100%
              </p>
              <p style={{ margin: 0, fontSize: "0.9em", color: "#666" }}>
                Current total: <strong>{(totalWeight * 100).toFixed(1)}%</strong>
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Weight inputs */}
      {competencies.map((comp, idx) => (
        <div
          key={comp.competencyId || comp.tempId}
          style={{ padding: "12px", backgroundColor: "#d9dff9", borderRadius: "8px", border: "1px solid #ddd" }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontWeight: 500 }}>{comp.name}</span>
            <WeightInput
              weight={comp.weight}
              onChange={(val) => handleWeightChange(idx, val)}
              disabled={!isAdmin}
            />
          </div>
          <p style={{ marginTop: "8px", color: "#555", lineHeight: "1.4" }}>
             <div className="rich-text-content" style={{ fontSize: '0.875rem', color: '#555' }}>
                          <div dangerouslySetInnerHTML={{ __html: comp.definition }} />
                        </div>
          </p>
        </div>
      ))}
    </div>

    {/* Save Configuration Button */}
    <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-end' }}>
      <button
        style={{
                backgroundColor: '#4caf50',
                color: 'white',
                border: 'none',
                borderRadius: '9999px',          // full pill
                padding: '4px 14px',
                fontSize: '0.98rem',
                fontWeight: 500,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
              }}
        onClick={saveCompetencyConfig}
        disabled={competencies.some(comp => !comp.name?.trim() || !comp.definition?.trim()) || !weightSetupComplete}
      >
        Save Configuration
      </button>
    </div>
  </div>
)}

    {/* Role-based view switching */}
    {!readOnlyReport && (isAdmin || hasManager) && (
      <div style={{ marginBottom: '24px' }}>
        <div style={{ 
          display: 'flex', 
          gap: '12px', 
          borderBottom: '2px solid #e0e0e0',
          marginBottom: '20px'
        }}>
          <button
            onClick={() => setViewMode('staff')}
            style={{
              padding: '10px 20px',
              backgroundColor: viewMode === 'staff' ? '#033ca8' : '#d7d1d1',
              color: viewMode === 'staff' ? 'white' : '#666',
              border: 'none',
              borderRadius: '4px 4px 0 0',
              cursor: 'pointer',
              fontWeight: 500
            }}
          >
            My Self-Assessment
          </button>
          <button
            onClick={() => setViewMode('manager_review')}
            style={{
              padding: '10px 20px',
              backgroundColor: viewMode === 'manager_review' ? '#033ca8' : '#d7d1d1',
              color: viewMode === 'manager_review' ? 'white' : '#666',
              border: 'none',
              borderRadius: '4px 4px 0 0',
              cursor: 'pointer',
              fontWeight: 500
            }}
          >
            Review Team Members
          </button>
        </div>
      </div>
    )}

    {/* Staff Self-Assessment View */}
    {(viewMode === 'staff' || (!isAdmin && !hasManager)) && (
      <section style={{ marginBottom: '2rem', backgroundColor: '#e9f8e8', padding: '1rem', borderRadius: '8px' }}>
        <h4 style={{ color: '#2c6693', marginBottom: '1rem' }}>
          Self Leadership/Behaviour Assessment
        </h4>
        
        {competencies.some(c => c.managerRatings?.[0] || c.managerRatings?.[1]) && (
          <div style={{
            padding: '12px',
            backgroundColor: '#e3f2fd',
            borderRadius: '6px',
            marginBottom: '16px',
            borderLeft: '3px solid #2196f3'
          }}>
            <strong>📋 Manager's Review Available:</strong> Please review your manager's assessment below and provide your feedback.
          </div>
        )}

        <div style={{ backgroundColor: 'white', padding: '1rem', borderRadius: '6px' }}>
          
          {/* Mid-Year Section */}
          {isMidYearActive && (
            <div style={{ marginBottom: '24px' }}>
              <div style={{ fontSize: "18px", fontWeight: 600, color: "#2c6693", marginBottom: "16px" }}>
                Mid-Year Review
                <div style={{ color: "green", fontWeight: "600", marginBottom: "8px", fontSize: "14px" }}>
                  In reviewing now...
                </div>
              </div>
              
              {competencies.map((comp, idx) => {
                const hasManagerRating = comp.managerRatings?.[0] !== null && comp.managerRatings?.[0] !== undefined;
                const hasStaffRating = comp.isSubmittedMidYear === true;
                const isFinalized = comp.finalApprovalStatus === 'finalized';
                
                if (hasManagerRating) {
                  return (
                    <StaffFinalizationView
                      key={idx}
                      competency={comp}
                      index={idx}
                      period={0}
                      onSave={handleStaffFinalization}
                      isSubmitting={isSubmitting}
                      isReadOnly={isReadOnly}
                      isFinalized={isFinalized}
                    />
                  );
                } else if (hasStaffRating && !hasManagerRating) {
                  return (
                    <div key={idx} style={{
                      padding: '16px',
                      backgroundColor: '#fff3e0',
                      borderRadius: '8px',
                      marginBottom: '16px',
                      borderLeft: '3px solid #ff9800'
                    }}>
                      <strong style={{ fontSize: '1rem', display: 'block', marginBottom: '8px' }}>{comp.name}</strong>
                      <p style={{ margin: '0 0 8px 0', fontSize: '0.85rem', color: '#666' }}>
                        <div className="rich-text-content" style={{ fontSize: '0.875rem', color: '#555' }}>
                        <div dangerouslySetInnerHTML={{ __html: comp.definition }} />
                        </div>
                      </p>
                      <div style={{ 
                        padding: '12px', 
                        backgroundColor: '#f5f5f5', 
                        borderRadius: '6px',
                        marginTop: '8px'
                      }}>
                        <div>Your Rating: <strong>{comp.ratings[0]}/5</strong></div>
                        <div>Your Comment: <em>{comp.comments[0] || 'No comment'}</em></div>
                      </div>
                      <p style={{ marginTop: '12px', color: '#ff9800', fontSize: '0.85rem' }}>
                        ⏳ Your self-assessment has been submitted. Waiting for manager's review...
                      </p>
                    </div>
                  );
                } else {
                  return (
                    <div
                      key={idx}
                      style={{
                        padding: "12px",
                        borderRadius: "8px",
                        backgroundColor: "#d7f3e3",
                        border: "1px solid #ddd",
                        position: 'relative',
                        marginBottom: '16px'
                      }}
                    >
                      <div style={{ marginBottom: '8px' }}>
                        <div style={{ fontWeight: 500, fontSize: '1rem', color: '#2c6693' }}>{comp.name}</div>
                        <div className="rich-text-content" style={{ fontSize: '0.875rem', color: '#555' }}>
                          <div dangerouslySetInnerHTML={{ __html: comp.definition }} />
                        </div>
                      </div>

                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
                        <RatingInput
                          value={comp.ratings[0]}
                          onChange={(val) => handleRatingChange(idx, 0, val)}
                          disabled={readOnlyReport || isReadOnly || !weightSetupComplete || comp.ratingsLocked?.[0]}
                          locked={readOnlyReport || comp.ratingsLocked?.[0]}
                          ariaLabel={`Mid-Year rating for ${comp.name}`}
                          orgUnitId={topOrgUnit || orgUnitId}  
                        />
                        
                        {savedIndicators[`${idx}-0`]?.show && (
                          <div style={{
                            padding: '2px 10px',
                            backgroundColor: colors.green100,
                            color: colors.green800,
                            borderRadius: '16px',
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            border: `1px solid ${colors.green300}`,
                            whiteSpace: 'nowrap'
                          }}>
                            ✓ Saved
                          </div>
                        )}
                        
                        {!readOnlyReport && !comp.ratingsLocked?.[0] && (
                          <Button
                            small
                            primary
                            onClick={() => handleSaveRating(idx, 0)}
                            disabled={!comp.ratings[0] || !comp.comments[0]?.trim()}
                          >
                            Save
                          </Button>
                        )}
                      </div>

                      <CommentInput
                        value={comp.comments[0]}
                        onChange={(val) => {
                          setCompetencies(prev => {
                            const newComps = [...prev];
                            newComps[idx].comments[0] = val;
                            return newComps;
                          });
                        }}
                        placeholder="Add comment for Mid-Year"
                        disabled={readOnlyReport || isReadOnly || !weightSetupComplete || comp.ratingsLocked?.[0]}
                      />
                    </div>
                  );
                }
              })}
            </div>
          )}

          {/* End-Year Section */}
          {isEndYearActive && (
            <div style={{ marginBottom: '24px' }}>
              <div style={{ fontSize: "18px", fontWeight: 600, color: "#2c6693", marginBottom: "16px" }}>
                End-Year Review
                <div style={{ color: "green", fontWeight: "600", marginBottom: "8px", fontSize: "14px" }}>
                  In reviewing now...
                </div>
              </div>
              
              {competencies.map((comp, idx) => {
                const hasManagerRating = comp.managerRatings?.[1] !== null && comp.managerRatings?.[1] !== undefined;
                const hasStaffRating = comp.isSubmittedEndYear === true;
                const isFinalized = comp.finalApprovalStatus === 'finalized';
                
                if (hasManagerRating) {
                  return (
                    <StaffFinalizationView
                      key={idx}
                      competency={comp}
                      index={idx}
                      period={1}
                      onSave={handleStaffFinalization}
                      isSubmitting={isSubmitting}
                      isReadOnly={isReadOnly}
                      isFinalized={isFinalized}
                    />
                  );
                } else if (hasStaffRating && !hasManagerRating) {
                  return (
                    <div key={idx} style={{
                      padding: '16px',
                      backgroundColor: '#fff3e0',
                      borderRadius: '8px',
                      marginBottom: '16px',
                      borderLeft: '3px solid #ff9800'
                    }}>
                      <strong style={{ fontSize: '1rem', display: 'block', marginBottom: '8px' }}>{comp.name}</strong>
                      <p style={{ margin: '0 0 8px 0', fontSize: '0.85rem', color: '#666' }}>
                        <div className="rich-text-content" style={{ fontSize: '0.875rem', color: '#555' }}>
                          <div dangerouslySetInnerHTML={{ __html: comp.definition }} />
                        </div>
                      </p>
                      <div style={{ 
                        padding: '12px', 
                        backgroundColor: '#f5f5f5', 
                        borderRadius: '6px',
                        marginTop: '8px'
                      }}>
                        <div>Your Rating: <strong>{comp.ratings[1]}/5</strong></div>
                        <div>Your Comment: <em>{comp.comments[1] || 'No comment'}</em></div>
                      </div>
                      <p style={{ marginTop: '12px', color: '#ff9800', fontSize: '0.85rem' }}>
                        ⏳ Your self-assessment has been submitted. Waiting for manager's review...
                      </p>
                    </div>
                  );
                } else {
                  return (
                    <div
                      key={idx}
                      style={{
                        padding: "12px",
                        borderRadius: "8px",
                        backgroundColor: "#d7f3e3",
                        border: "1px solid #ddd",
                        position: 'relative',
                        marginBottom: '16px'
                      }}
                    >
                      <div style={{ marginBottom: '8px' }}>
                        <div style={{ fontWeight: 500, fontSize: '1rem', color: '#2c6693' }}>{comp.name}</div>
                        <div style={{ fontSize: '0.875rem', color: '#555', fontStyle: 'italic' }}>
                          <div className="rich-text-content" style={{ fontSize: '0.875rem', color: '#555' }}>
                          <div dangerouslySetInnerHTML={{ __html: comp.definition }} />
                        </div>
                        </div>
                      </div>

                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
                        <RatingInput
                          value={comp.ratings[1]}
                          onChange={(val) => handleRatingChange(idx, 1, val)}
                          disabled={readOnlyReport || isReadOnly || !weightSetupComplete || comp.ratingsLocked?.[1]}
                          locked={readOnlyReport || comp.ratingsLocked?.[1]}
                          ariaLabel={`End-Year rating for ${comp.name}`}
                          orgUnitId={topOrgUnit || orgUnitId}
                        />
                        
                        {savedIndicators[`${idx}-1`]?.show && (
                          <div style={{
                            padding: '2px 10px',
                            backgroundColor: colors.green100,
                            color: colors.green800,
                            borderRadius: '16px',
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            border: `1px solid ${colors.green300}`,
                            whiteSpace: 'nowrap'
                          }}>
                            ✓ Saved
                          </div>
                        )}
                        
                        {!readOnlyReport && !comp.ratingsLocked?.[1] && (
                          <Button
                            small
                            primary
                            onClick={() => handleSaveRating(idx, 1)}
                            disabled={!comp.ratings[1] || !comp.comments[1]?.trim()}
                          >
                            Save
                          </Button>
                        )}
                      </div>

                      <CommentInput
                        value={comp.comments[1]}
                        onChange={(val) => {
                          setCompetencies(prev => {
                            const newComps = [...prev];
                            newComps[idx].comments[1] = val;
                            return newComps;
                          });
                        }}
                        placeholder="Add comment for End-Year"
                        disabled={readOnlyReport || isReadOnly || !weightSetupComplete || comp.ratingsLocked?.[1]}
                      />
                    </div>
                  );
                }
              })}
            </div>
          )}

          {/* Total Score Section */}
          <div
            style={{
              padding: "16px",
              backgroundColor: "#f8f9fa",
              borderRadius: "8px",
              border: `2px solid ${colors.white}`,
            }}
          >
            <div style={{
              fontSize: "18px",
              fontWeight: 600,
              color: "#2c6693",
              marginBottom: "16px",
              textAlign: "center"
            }}>
              Total Score
            </div>
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "16px",
              flexWrap: "wrap"
            }}>
              {competencies.map((comp, idx) => (
                <div
                  key={idx}
                  style={{
                    flex: "1",
                    minWidth: "120px",
                    padding: "12px",
                    backgroundColor: "#ddf6de",
                    borderRadius: "8px",
                    border: "1px solid #ddd",
                    textAlign: "center"
                  }}
                >
                  <div style={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#2c6693",
                    marginBottom: "8px"
                  }}>
                    {comp.name} Score
                  </div>
                  <div style={{
                    fontSize: "16px",
                    fontWeight: "bold",
                    color: "#1b5e20"
                  }}>
                    {calculateScore(comp).toFixed(2)}
                  </div>
                </div>
              ))}

              <div
                style={{
                  flex: "1",
                  minWidth: "140px",
                  padding: "16px",
                  backgroundColor: "#2c6693",
                  borderRadius: "8px",
                  border: "1px solid #1a4d7a",
                  textAlign: "center",
                  color: "white"
                }}
              >
                <div style={{
                  fontSize: "14px",
                  fontWeight: 600,
                  marginBottom: "8px"
                }}>
                  Overall Total Score
                </div>
                <div style={{
                  fontSize: "20px",
                  fontWeight: "bold"
                }}>
                  {calculateFinalScore()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    )}
 
      {/* Manager Review View */}
      {viewMode === 'manager_review' && (isAdmin || hasManager) && (
        <section style={{ marginBottom: '2rem', backgroundColor: '#d8dff9', padding: '1rem', borderRadius: '8px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <h4 style={{ color: '#2c6693', margin: 0 }}>
              Team Members' Leadership Assessments ({directReportAssessments.length})
            </h4>
            <div style={{ fontSize: '0.875rem', color: '#666' }}>
              <span>💡 Click on any staff card to expand/collapse</span>
            </div>
          </div>
          
          {directReportAssessments.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', backgroundColor: 'white', borderRadius: '8px' }}>
              <p>No team members have submitted assessments yet.</p>
            </div>
          ) : (
            directReportAssessments.map((assessment, idx) => {
              const staff = staffList.find(s => s.teiId === assessment.teiId);
              const period = isMidYearActive ? 0 : (isEndYearActive ? 1 : null);
              if (period === null) return null;
              
              const needsReview = assessment.competencies.some(c => 
                c.ratings[period] !== null && !c.managerRatings?.[period]
              );
              const isExpanded = expandedManagerStaff === idx;
              
              // Count how many competencies need review for the CURRENT period
              const needsReviewCount = assessment.competencies.filter(c => 
                c.ratings[period] !== null && !c.managerRatings?.[period]
              ).length;
              
              // Count completed reviews for the CURRENT period
              const completedCount = assessment.competencies.filter(c => 
                c.managerRatings?.[period] !== null
              ).length;
              
              // Check if any competency is finalized
              const hasFinalized = assessment.competencies.some(c => 
                c.finalApprovalStatus === 'finalized'
              );
              
              return (
                <div key={idx} style={{
                  marginBottom: '16px',
                  border: hasFinalized ? '2px solid #4caf50' : (needsReview ? '2px solid #ff9800' : '1px solid #e0e0e0'),
                  borderRadius: '8px',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease'
                }}>
                  {/* Staff Header - Click to expand/collapse */}
                  <div
                    onClick={() => setExpandedManagerStaff(isExpanded ? null : idx)}
                    style={{
                      padding: '16px 20px',
                      backgroundColor: hasFinalized ? '#e8f5e9' : (needsReview ? '#fff3e0' : '#f5f5f5'),
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '12px',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!isExpanded) {
                        e.currentTarget.style.backgroundColor = hasFinalized ? '#c8e6c9' : (needsReview ? '#ffe0b2' : '#e0e0e0');
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isExpanded) {
                        e.currentTarget.style.backgroundColor = hasFinalized ? '#e8f5e9' : (needsReview ? '#fff3e0' : '#f5f5f5');
                      }
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '50%',
                        backgroundColor: hasFinalized ? '#4caf50' : (needsReview ? '#ff9800' : '#2c6693'),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '1.2rem'
                      }}>
                        {staff?.firstName?.charAt(0)}{staff?.surname?.charAt(0)}
                      </div>
                      <div>
                        <strong style={{ fontSize: '1.1rem' }}>{staff?.firstName} {staff?.surname}</strong>
                        <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>
                          {assessment.positionTitle || 'Staff Member'}
                        </div>
                      </div>
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                      {/* Progress badges */}
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {hasFinalized && (
                          <span style={{
                            padding: '4px 12px',
                            backgroundColor: '#4caf50',
                            color: 'white',
                            borderRadius: '20px',
                            fontSize: '0.75rem',
                            fontWeight: 'bold'
                          }}>
                            ✓ Finalized
                          </span>
                        )}
                        {!hasFinalized && needsReviewCount > 0 && (
                          <span style={{
                            padding: '4px 12px',
                            backgroundColor: '#ff9800',
                            color: 'white',
                            borderRadius: '20px',
                            fontSize: '0.75rem',
                            fontWeight: 'bold'
                          }}>
                            {needsReviewCount} Need{needsReviewCount !== 1 ? '' : 's'} Review
                          </span>
                        )}
                        {completedCount > 0 && !hasFinalized && (
                          <span style={{
                            padding: '4px 12px',
                            backgroundColor: '#4caf50',
                            color: 'white',
                            borderRadius: '20px',
                            fontSize: '0.75rem',
                            fontWeight: 'bold'
                          }}>
                            {completedCount} Reviewed
                          </span>
                        )}
                      </div>
                      
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        color: hasFinalized ? '#4caf50' : (needsReview ? '#ff9800' : '#666')
                      }}>
                        <span style={{ fontSize: '0.85rem' }}>
                          {isExpanded ? '▼ Click to collapse' : '▶ Click to expand'}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Expanded Content - Only shown when expanded */}
                  {isExpanded && (
                    <div style={{ 
                      padding: '20px', 
                      backgroundColor: 'white',
                      borderTop: `1px solid ${hasFinalized ? '#4caf50' : (needsReview ? '#ff9800' : '#e0e0e0')}`
                    }}>
                      {/* Progress Summary Bar */}
                      <div style={{
                        marginBottom: '20px',
                        padding: '12px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px'
                      }}>
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          marginBottom: '8px',
                          fontSize: '0.85rem'
                        }}>
                          <span>Review Progress ({period === 0 ? 'Mid-Year' : 'End-Year'})</span>
                          <span>
                            <strong>{completedCount}</strong> of <strong>{assessment.competencies.length}</strong> competencies reviewed
                            {hasFinalized && <span style={{ color: '#4caf50', marginLeft: '8px' }}>✓ All Finalized</span>}
                          </span>
                        </div>
                        <div style={{
                          height: '8px',
                          backgroundColor: '#e0e0e0',
                          borderRadius: '4px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${(completedCount / assessment.competencies.length) * 100}%`,
                            height: '100%',
                            backgroundColor: hasFinalized ? '#4caf50' : (completedCount === assessment.competencies.length ? '#4caf50' : '#ff9800'),
                            transition: 'width 0.3s ease'
                          }} />
                        </div>
                      </div>
                      
                      {/* Competencies List */}
                      {assessment.competencies.map((comp, compIdx) => {
                        // Check if this competency has been finalized by the staff
                        const hasCompFinalized = comp.finalApprovalStatus === 'finalized';
                        
                        // Return the ManagerCompetencyReview with hasFinalized prop
                        return (
                          <ManagerCompetencyReview
                            key={compIdx}
                            competency={comp}
                            index={compIdx}
                            period={period}
                            staffName={staff?.firstName || 'Staff'}
                            onSave={(compIdx, period, data) => handleManagerRatingSave(idx, compIdx, period, data)}
                            isSubmitting={isSubmitting}
                            isReadOnly={false} // Always allow editing unless finalized
                            hasFinalized={hasCompFinalized} // Pass the finalized status
                            orgUnitId={topOrgUnit || orgUnitId}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </section>
      )}

      {/* Team Summary Section - Show for managers */}
      {!readOnlyReport && (isAdmin || hasManager) && directReportAssessments.length > 0 && (
        <section style={{ marginBottom: '2rem', backgroundColor: '#f8f9fa', padding: '1rem', borderRadius: '8px' }}>
          <h4 style={{ color: '#2c6693', marginBottom: '1rem' }}>
            Team Summary
          </h4>
          <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: '#2c6693',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold'
              }}>
                {directReportAssessments.length}
              </div>
              <div>
                <div style={{ fontSize: '0.875rem', color: '#666' }}>Total Staff</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#2c6693' }}>
                  {directReportAssessments.length} members
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: '#ff9800',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold'
              }}>
                {directReportAssessments.filter(a =>
                  a.competencies?.some(c => c.ratings[0] !== null && !c.managerRatings?.[0])
                ).length}
              </div>
              <div>
                <div style={{ fontSize: '0.875rem', color: '#666' }}>Pending Manager Review</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#ff9800' }}>
                  {directReportAssessments.filter(a =>
                    a.competencies?.some(c => c.ratings[0] !== null && !c.managerRatings?.[0])
                  ).length} staff
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: '#4caf50',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold'
              }}>
                {directReportAssessments.filter(a =>
                  a.competencies.every(c => c.managerRatings?.[0] !== null)
                ).length}
              </div>
              <div>
                <div style={{ fontSize: '0.875rem', color: '#666' }}>Manager Review Completed</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#4caf50' }}>
                  {directReportAssessments.filter(a =>
                    a.competencies.every(c => c.managerRatings?.[0] !== null)
                  ).length} staff
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: '#9c27b0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold'
              }}>
                {directReportAssessments.filter(a =>
                  a.competencies.some(c => c.finalApprovalStatus === 'finalized')
                ).length}
              </div>
              <div>
                <div style={{ fontSize: '0.875rem', color: '#666' }}>Finalized by Staff</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#9c27b0' }}>
                  {directReportAssessments.filter(a =>
                    a.competencies.some(c => c.finalApprovalStatus === 'finalized')
                  ).length} staff
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Global Notification */}
      {notification.visible && (
        <div style={{ position: 'fixed', bottom: '20px', right: '200px', zIndex: 10000, width: '400px' }}>
          <AlertBar
            {...(notification.type === 'success' ? { success: true } : 
                notification.type === 'warning' ? { warning: true } : 
                { critical: true })}
            duration={notification.type === 'warning' ? 3000 : 4000}
            onHidden={() => setNotification({ visible: false })}
          >
            {`${notification.title}${notification.details ? `: ${notification.details}` : ''}`}
          </AlertBar>
        </div>      
      )}
    </div>
  );
}

export default LeadershipManagementPage;
